import { Component } from "@theme/component";
import {
  fetchConfig,
  preloadImage,
  onAnimationEnd,
  yieldToMainThread,
} from "@theme/utilities";
import {
  ThemeEvents,
  CartAddEvent,
  CartErrorEvent,
  CartUpdateEvent,
  VariantUpdateEvent,
} from "@theme/events";
import { cartPerformance } from "@theme/performance";
import { morph } from "@theme/morph";

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

const FILE_UPLOAD_FEEDBACK_DURATION = 5000;

const IMAGE_UPLOAD_EXTENSIONS = new Set([
  ".jpg",
  ".jpeg",
  ".png",
  ".webp",
  ".avif",
  ".heic",
  ".heif",
]);
const SKIP_IMAGE_DECODE_VALIDATION_EXTENSIONS = new Set([".heic", ".heif"]);
const MIME_TYPE_EXTENSIONS = new Map([
  ["image/jpeg", [".jpg", ".jpeg"]],
  ["image/png", [".png"]],
  ["image/webp", [".webp"]],
  ["image/avif", [".avif"]],
  ["image/heic", [".heic"]],
  ["image/heif", [".heif"]],
]);
const BLOCKED_UPLOAD_EXTENSIONS = new Set([
  ".svg",
  ".html",
  ".htm",
  ".js",
  ".mjs",
  ".php",
  ".exe",
  ".app",
  ".dmg",
]);
const UPLOAD_FILE_NAME_MAX_LENGTH = 120;
const FILE_UPLOAD_DEFAULT_MESSAGES = {
  required: "please upload a file before adding this item to cart.",
  invalid: "please upload a valid file.",
  unsupported: "please upload a supported file type.",
  size: "please upload a file smaller than [max_size] mb.",
  validating: "validating file...",
  limitOne: "you can upload up to [max] files. 1 extra file was not added.",
  limitMany:
    "you can upload up to [max] files. [count] extra files were not added.",
  invalidFilesOne: "1 file could not be uploaded.",
  invalidFilesMany: "[count] files could not be uploaded.",
  fileCount: "[count] / [max] files added",
  filesSelected: "[count] files selected",
};
const PRODUCT_TEXTAREA_REQUIRED_MESSAGE =
  "please add this information before adding the item to cart.";

/**
 * @param {string} template
 * @param {Record<string, string | number>} values
 * @returns {string}
 */
function formatMessageTemplate(template, values = {}) {
  return Object.entries(values).reduce(
    (message, [key, value]) =>
      message
        .split(`[${key}]`)
        .join(String(value))
        .split(`{{ ${key} }}`)
        .join(String(value))
        .split(`{{${key}}}`)
        .join(String(value)),
    template,
  );
}

/**
 * Normalizes product textarea inputs before validity checks and cart submission.
 * @param {HTMLFormElement} form
 */
function normalizeProductTextareaInputs(form) {
  const textareas = /** @type {HTMLTextAreaElement[]} */ (
    Array.from(form.elements).filter(
      (element) =>
        element instanceof HTMLTextAreaElement &&
        element.matches("textarea[data-product-textarea-input]"),
    )
  );

  for (const textarea of textareas) {
    const normalizedValue = textarea.value
      .replace(/\r\n?/g, "\n")
      .replace(/\n(?:[ \t]*\n){2,}/g, "\n\n")
      .trim();

    textarea.value = normalizedValue;
    textarea.setCustomValidity("");
    setProductTextareaError(textarea, "");

    if (textarea.required && !normalizedValue) {
      const errorMessage = getProductTextareaErrorMessage(textarea);
      textarea.setCustomValidity(errorMessage);
      setProductTextareaError(textarea, errorMessage);
    }
  }
}

/**
 * @param {HTMLInputElement} input
 * @returns {string}
 */
function getFutCardLineItemTextErrorMessage(input) {
  if (input.dataset.requiredMessage) {
    return input.dataset.requiredMessage;
  }

  const error = input
    .closest("fut-card-line-item-text")
    ?.querySelector("[data-fut-card-line-item-text-error]");

  if (error instanceof HTMLElement && error.dataset.defaultError) {
    return error.dataset.defaultError;
  }

  return "enter the name for your card.";
}

/**
 * @param {HTMLInputElement} input
 * @param {string} message
 */
function setFutCardLineItemTextError(input, message) {
  const container = input.closest("fut-card-line-item-text");
  if (!(container instanceof HTMLElement)) return;

  const error = container.querySelector("[data-fut-card-line-item-text-error]");
  container.toggleAttribute("text-input-error", Boolean(message));

  if (!(error instanceof HTMLElement)) return;

  error.textContent = message || error.dataset.defaultError || "";
  error.hidden = !message;
}

/**
 * Normalizes fut card single-line text inputs before validity checks and cart submission.
 * @param {HTMLFormElement} form
 */
function normalizeFutCardLineItemTextInputs(form) {
  const inputs = /** @type {HTMLInputElement[]} */ (
    Array.from(form.elements).filter(
      (element) =>
        element instanceof HTMLInputElement &&
        element.matches("input[data-fut-card-line-item-text]"),
    )
  );

  for (const input of inputs) {
    const normalizedValue = input.value.trim();
    input.value = normalizedValue;
    input.setCustomValidity("");
    setFutCardLineItemTextError(input, "");

    if (input.required && !normalizedValue) {
      const errorMessage = getFutCardLineItemTextErrorMessage(input);
      input.setCustomValidity(errorMessage);
      setFutCardLineItemTextError(input, errorMessage);
    }
  }
}

const FUT_CARD_PLAYER_POSITION_PATTERN = /^[A-Z]{1,6}$/;

/**
 * @param {HTMLInputElement} input
 * @returns {string}
 */
function getFutCardPlayerPositionErrorMessage(input) {
  if (input.dataset.requiredMessage) {
    return input.dataset.requiredMessage;
  }

  const error = input
    .closest("fut-card-player-position")
    ?.querySelector("[data-fut-card-player-position-error]");

  if (error instanceof HTMLElement && error.dataset.defaultError) {
    return error.dataset.defaultError;
  }

  return "choose or enter a position for your card.";
}

/**
 * @param {HTMLInputElement} input
 * @returns {string}
 */
function getFutCardPlayerPositionInvalidMessage(input) {
  if (input.dataset.invalidMessage) {
    return input.dataset.invalidMessage;
  }

  return "enter 1–4 letters for your custom position.";
}

/**
 * @param {HTMLInputElement} input
 * @param {string} message
 */
function setFutCardPlayerPositionError(input, message) {
  const container = input.closest("fut-card-player-position");
  if (!(container instanceof HTMLElement)) return;

  const error = container.querySelector(
    "[data-fut-card-player-position-error]",
  );
  container.toggleAttribute("position-input-error", Boolean(message));

  if (!(error instanceof HTMLElement)) return;

  error.textContent = message || error.dataset.defaultError || "";
  error.hidden = !message;
}

/**
 * @param {HTMLInputElement} input
 */
function clearFutCardPlayerPositionError(input) {
  input.setCustomValidity("");
  setFutCardPlayerPositionError(input, "");
}

/**
 * @param {HTMLInputElement} input
 * @returns {string}
 */
function normalizeFutCardPlayerPositionValue(input) {
  return input.value.trim().toUpperCase();
}

/**
 * @param {HTMLInputElement} input
 * @returns {string}
 */
function validateFutCardPlayerPositionInput(input) {
  const normalizedValue = normalizeFutCardPlayerPositionValue(input);
  input.value = normalizedValue;
  input.setCustomValidity("");
  setFutCardPlayerPositionError(input, "");

  if (input.required && !normalizedValue) {
    const errorMessage = getFutCardPlayerPositionErrorMessage(input);
    input.setCustomValidity(errorMessage);
    setFutCardPlayerPositionError(input, errorMessage);
    return errorMessage;
  }

  if (
    normalizedValue &&
    !FUT_CARD_PLAYER_POSITION_PATTERN.test(normalizedValue)
  ) {
    const errorMessage = getFutCardPlayerPositionInvalidMessage(input);
    input.setCustomValidity(errorMessage);
    setFutCardPlayerPositionError(input, errorMessage);
    return errorMessage;
  }

  return "";
}

/**
 * @param {HTMLInputElement} input
 * @returns {string}
 */
function getFutCardImageOptionsErrorMessage(input) {
  if (input.dataset.requiredMessage) {
    return input.dataset.requiredMessage;
  }

  const error = input
    .closest("fut-card-image-options")
    ?.querySelector("[data-fut-card-image-options-error]");

  if (error instanceof HTMLElement && error.dataset.defaultError) {
    return error.dataset.defaultError;
  }

  return "choose an option.";
}

/**
 * @param {HTMLInputElement} input
 * @param {string} message
 */
function setFutCardImageOptionsError(input, message) {
  const container = input.closest("fut-card-image-options");
  if (!(container instanceof HTMLElement)) return;

  const error = container.querySelector("[data-fut-card-image-options-error]");
  container.toggleAttribute("image-options-error", Boolean(message));

  if (!(error instanceof HTMLElement)) return;

  error.textContent = message || error.dataset.defaultError || "";
  error.hidden = !message;
}

/**
 * @param {HTMLInputElement} input
 */
function clearFutCardImageOptionsError(input) {
  input.setCustomValidity("");
  setFutCardImageOptionsError(input, "");
}

/**
 * @param {HTMLInputElement} input
 * @returns {string}
 */
function validateFutCardImageOptionsInput(input) {
  const normalizedValue = input.value.trim();
  input.value = normalizedValue;
  input.setCustomValidity("");
  setFutCardImageOptionsError(input, "");

  if (input.required && !normalizedValue) {
    const errorMessage = getFutCardImageOptionsErrorMessage(input);
    input.setCustomValidity(errorMessage);
    setFutCardImageOptionsError(input, errorMessage);
    return errorMessage;
  }

  return "";
}

/**
 * Normalizes fut card image option inputs before validity checks and cart submission.
 * @param {HTMLFormElement} form
 */
function normalizeFutCardImageOptionsInputs(form) {
  const inputs = /** @type {HTMLInputElement[]} */ (
    Array.from(form.elements).filter(
      (element) =>
        element instanceof HTMLInputElement &&
        element.matches("input[data-fut-card-image-options-property]"),
    )
  );

  for (const input of inputs) {
    validateFutCardImageOptionsInput(input);
  }
}

/**
 * Normalizes fut card player position inputs before validity checks and cart submission.
 * @param {HTMLFormElement} form
 */
function normalizeFutCardPlayerPositionInputs(form) {
  const inputs = /** @type {HTMLInputElement[]} */ (
    Array.from(form.elements).filter(
      (element) =>
        element instanceof HTMLInputElement &&
        element.matches("input[data-fut-card-player-position]"),
    )
  );

  for (const input of inputs) {
    validateFutCardPlayerPositionInput(input);
  }
}

/**
 * @param {string | undefined} value
 * @returns {string[]}
 */
function parseFutCardStatLabelPreset(value) {
  return (value ?? "")
    .split(",")
    .map((label) => label.trim().toUpperCase())
    .filter(Boolean);
}

const FUT_CARD_STAT_LABEL_LANGUAGES = ["en", "pt", "de", "fr", "es"];

/**
 * @param {HTMLElement} stateRoot
 * @returns {{ field: Record<string, string[]>, goalkeeper: Record<string, string[]> }}
 */
function parseFutCardStatLabelsByLanguage(stateRoot) {
  /** @type {Record<string, string[]>} */
  const field = {};
  /** @type {Record<string, string[]>} */
  const goalkeeper = {};

  for (const lang of FUT_CARD_STAT_LABEL_LANGUAGES) {
    const datasetSuffix =
      lang === "en" ? "" : lang.charAt(0).toUpperCase() + lang.slice(1);
    const fieldKey = datasetSuffix
      ? `fieldStatLabels${datasetSuffix}`
      : "fieldStatLabels";
    const goalkeeperKey = datasetSuffix
      ? `goalkeeperStatLabels${datasetSuffix}`
      : "goalkeeperStatLabels";

    field[lang] = parseFutCardStatLabelPreset(stateRoot.dataset[fieldKey]);
    goalkeeper[lang] = parseFutCardStatLabelPreset(
      stateRoot.dataset[goalkeeperKey],
    );
  }

  return { field, goalkeeper };
}

/**
 * @param {ParentNode} container
 * @returns {HTMLElement | null}
 */
function getFutCardStatsStateRoot(container) {
  if (!(container instanceof HTMLElement)) return null;
  if (container.classList.contains("fut-card-stats")) return container;

  const statsElement = container.matches("fut-card-stats")
    ? container
    : container.querySelector("fut-card-stats");

  if (!(statsElement instanceof HTMLElement)) return null;

  const inner = statsElement.querySelector(".fut-card-stats");
  return inner instanceof HTMLElement ? inner : statsElement;
}

/**
 * @param {ParentNode} container
 * @returns {{ min: number, max: number }}
 */
function getFutCardStatsBounds(container) {
  const root = getFutCardStatsStateRoot(container);

  const min = Number.parseInt(
    root instanceof HTMLElement ? (root.dataset.minStat ?? "1") : "1",
    10,
  );
  const max = Number.parseInt(
    root instanceof HTMLElement ? (root.dataset.maxStat ?? "99") : "99",
    10,
  );

  return {
    min: Number.isNaN(min) ? 1 : min,
    max: Number.isNaN(max) ? 99 : max,
  };
}

/**
 * @param {HTMLInputElement} input
 * @returns {number | null}
 */
function parseFutCardStatValue(input) {
  const parsed = Number.parseInt(input.value, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * @param {HTMLInputElement} input
 * @param {{ min: number, max: number }} bounds
 */
function normalizeFutCardStatValueInput(input, bounds) {
  const { max } = bounds;
  let digits = input.value.replace(/\D/g, "").slice(0, 2);

  if (!digits) {
    input.value = "";
    return;
  }

  const value = Number.parseInt(digits, 10);
  if (!Number.isNaN(value) && value > max) {
    input.value = String(max);
    return;
  }

  input.value = digits;
}

/**
 * @param {string} label
 * @returns {string}
 */
function normalizeFutCardStatLabelValue(label) {
  return label
    .trim()
    .toUpperCase()
    .replace(/[^\p{L}\p{N}]/gu, "")
    .slice(0, 4);
}

/**
 * @param {ParentNode} container
 * @param {string} message
 */
function setFutCardStatsError(container, message) {
  const root =
    container instanceof HTMLElement && container.matches("fut-card-stats")
      ? container
      : container.querySelector("fut-card-stats");

  if (!(root instanceof HTMLElement)) return;

  const error = root.querySelector("[data-fut-card-stats-error]");
  root.toggleAttribute("stats-input-error", Boolean(message));

  if (!(error instanceof HTMLElement)) return;

  error.hidden = !message;
  if (message) error.textContent = message;
}

/**
 * @param {HTMLElement} error
 * @param {string} key
 * @returns {string}
 */
function getFutCardStatsErrorMessage(error, key) {
  const datasetKey = `${key}Message`;
  const datasetMessage = error.dataset[datasetKey];
  if (datasetMessage) return datasetMessage;

  if (key === "required" && error.dataset.requiredMessage) {
    return error.dataset.requiredMessage;
  }

  return error.textContent.trim();
}

/**
 * @param {ParentNode} container
 * @returns {HTMLInputElement[]}
 */
function getFutCardStatValueInputs(container) {
  const root =
    container instanceof HTMLElement && container.matches("fut-card-stats")
      ? container
      : container.querySelector("fut-card-stats");

  if (!(root instanceof HTMLElement)) return [];

  return /** @type {HTMLInputElement[]} */ (
    Array.from(root.querySelectorAll("[data-fut-card-stat-value]"))
  );
}

/**
 * @param {ParentNode} container
 * @returns {HTMLInputElement[]}
 */
function getFutCardStatLabelInputs(container) {
  const root =
    container instanceof HTMLElement && container.matches("fut-card-stats")
      ? container
      : container.querySelector("fut-card-stats");

  if (!(root instanceof HTMLElement)) return [];

  return /** @type {HTMLInputElement[]} */ (
    Array.from(root.querySelectorAll("[data-fut-card-stat-label]"))
  );
}

/**
 * @param {ParentNode} container
 * @returns {number}
 */
function calculateFutCardStatsAverage(container) {
  const { min, max } = getFutCardStatsBounds(container);
  const values = getFutCardStatValueInputs(container)
    .map(parseFutCardStatValue)
    .filter((value) => value !== null);

  if (values.length === 0) return min;

  const sum = values.reduce((total, value) => total + value, 0);
  const average = Math.round(sum / values.length);
  return Math.min(max, Math.max(min, average));
}

/**
 * @param {ParentNode} container
 */
function syncFutCardStatPropertyNames(container) {
  const valueInputs = getFutCardStatValueInputs(container);
  const labelInputs = getFutCardStatLabelInputs(container);

  valueInputs.forEach((input, index) => {
    const labelInput = labelInputs[index];
    const normalizedLabel = normalizeFutCardStatLabelValue(
      labelInput?.value ?? "",
    );
    const fallbackLabel =
      input.dataset.defaultLabel?.trim() || `STAT ${index + 1}`;
    const propertyLabel = normalizedLabel || fallbackLabel;
    input.name = `properties[${propertyLabel}]`;
  });
}

/**
 * @param {ParentNode} container
 * @returns {string}
 */
function validateFutCardStatsInputs(container) {
  const root =
    container instanceof HTMLElement && container.matches("fut-card-stats")
      ? container
      : container.querySelector("fut-card-stats");

  if (!(root instanceof HTMLElement)) return "";

  const error = root.querySelector("[data-fut-card-stats-error]");
  const { min, max } = getFutCardStatsBounds(root);
  const valueInputs = getFutCardStatValueInputs(root);
  const labelInputs = getFutCardStatLabelInputs(root);
  const overallProperty = root.querySelector("[data-fut-card-overall-rating]");
  const overrideToggle = root.querySelector(
    "[data-fut-card-overall-override-toggle]",
  );
  const overallInput = root.querySelector("[data-fut-card-overall-input]");

  syncFutCardStatPropertyNames(root);

  for (const input of valueInputs) {
    const value = parseFutCardStatValue(input);

    input.setCustomValidity("");

    if (input.required && value === null) {
      const message =
        error instanceof HTMLElement
          ? getFutCardStatsErrorMessage(error, "required")
          : "fill in all stats.";
      input.setCustomValidity(message);
      setFutCardStatsError(root, message);
      return message;
    }

    if (value !== null && (value < min || value > max)) {
      const template =
        error instanceof HTMLElement
          ? getFutCardStatsErrorMessage(error, "invalidStat")
          : "stats must be between [min] and [max].";
      const message = formatMessageTemplate(template, { min, max });
      input.setCustomValidity(message);
      setFutCardStatsError(root, message);
      return message;
    }

    if (value !== null) input.value = String(value);
  }

  for (const input of labelInputs) {
    const normalizedLabel = normalizeFutCardStatLabelValue(input.value);
    input.value = normalizedLabel;
    input.setCustomValidity("");

    if (input.required && !normalizedLabel) {
      const message =
        error instanceof HTMLElement
          ? getFutCardStatsErrorMessage(error, "required")
          : "fill in all stats.";
      input.setCustomValidity(message);
      setFutCardStatsError(root, message);
      return message;
    }

    if (normalizedLabel && !/^[\p{L}\p{N}]{1,4}$/u.test(normalizedLabel)) {
      const message =
        error instanceof HTMLElement
          ? getFutCardStatsErrorMessage(error, "invalidLabel")
          : "use 1–4 letters for each abbreviation.";
      input.setCustomValidity(message);
      setFutCardStatsError(root, message);
      return message;
    }
  }

  syncFutCardStatPropertyNames(root);

  const average = calculateFutCardStatsAverage(root);
  const isOverride =
    overrideToggle instanceof HTMLInputElement && overrideToggle.checked;
  let overallValue = average;

  if (isOverride && overallInput instanceof HTMLInputElement) {
    const customValue = parseFutCardStatValue(overallInput);
    if (customValue === null) {
      const message =
        error instanceof HTMLElement
          ? getFutCardStatsErrorMessage(error, "required")
          : "fill in all stats.";
      overallInput.setCustomValidity(message);
      setFutCardStatsError(root, message);
      return message;
    }

    if (customValue < min || customValue > max) {
      const template =
        error instanceof HTMLElement
          ? getFutCardStatsErrorMessage(error, "invalidStat")
          : "stats must be between [min] and [max].";
      const message = formatMessageTemplate(template, { min, max });
      overallInput.setCustomValidity(message);
      setFutCardStatsError(root, message);
      return message;
    }

    overallValue = customValue;
    overallInput.value = String(customValue);
    overallInput.setCustomValidity("");
  }

  if (overallProperty instanceof HTMLInputElement) {
    overallProperty.value = String(overallValue);
    overallProperty.setCustomValidity("");

    if (overallProperty.required && !overallProperty.value) {
      const message =
        error instanceof HTMLElement
          ? getFutCardStatsErrorMessage(error, "required")
          : "fill in all stats.";
      overallProperty.setCustomValidity(message);
      setFutCardStatsError(root, message);
      return message;
    }
  }

  setFutCardStatsError(root, "");
  return "";
}

/**
 * Normalizes fut card stats inputs before validity checks and cart submission.
 * @param {HTMLFormElement} form
 */
function normalizeFutCardStatsInputs(form) {
  const statsBlocks = form.querySelectorAll("fut-card-stats");
  statsBlocks.forEach((statsBlock) => {
    validateFutCardStatsInputs(statsBlock);
  });
}

/**
 * @param {ParentNode | null | undefined} root
 * @returns {Set<string>}
 */
function getFutCardGoalkeeperPositionSet(root) {
  const positionRoot =
    root instanceof HTMLElement
      ? (root.querySelector(".fut-card-player-position") ??
        root.querySelector("fut-card-player-position"))
      : null;

  if (!(positionRoot instanceof HTMLElement)) return new Set();

  return new Set(
    parseFutCardStatLabelPreset(positionRoot.dataset.goalkeeperPositions),
  );
}

/**
 * @param {string} position
 * @param {ParentNode | null | undefined} root
 * @returns {boolean}
 */
function isFutCardGoalkeeperPosition(position, root) {
  const normalized = position.trim().toUpperCase();
  if (!normalized) return false;

  const checkedPreset = root?.querySelector(
    "[data-fut-card-position-preset]:checked, [data-fut-card-position-custom]:checked",
  );

  if (checkedPreset instanceof HTMLInputElement) {
    if (checkedPreset.matches("[data-fut-card-position-custom]")) return false;

    const goalkeeperRow = checkedPreset.closest(
      '[data-fut-card-position-row="goalkeeper"]',
    );
    if (goalkeeperRow) return true;

    const positionRow = checkedPreset.closest("[data-fut-card-position-row]");
    if (positionRow) return false;
  }

  return getFutCardGoalkeeperPositionSet(root).has(normalized);
}

/**
 * Normalizes product line item text inputs before validity checks and cart submission.
 * @param {HTMLFormElement} form
 */
function normalizeProductLineItemInputs(form) {
  normalizeProductTextareaInputs(form);
  normalizeFutCardLineItemTextInputs(form);
  normalizeFutCardImageOptionsInputs(form);
  normalizeFutCardPlayerPositionInputs(form);
  normalizeFutCardStatsInputs(form);
}

/**
 * @param {HTMLElement} combobox
 * @returns {string}
 */
function getComboboxErrorMessage(combobox) {
  const valueInput = combobox.querySelector("[data-combobox-value]");
  if (
    valueInput instanceof HTMLInputElement &&
    valueInput.dataset.requiredMessage
  ) {
    return valueInput.dataset.requiredMessage;
  }

  const error = combobox.querySelector("[data-combobox-error]");
  if (error instanceof HTMLElement && error.dataset.defaultError) {
    return error.dataset.defaultError;
  }

  return "select an option.";
}

/**
 * @param {HTMLElement} combobox
 * @param {string} message
 */
function setComboboxError(combobox, message) {
  combobox.toggleAttribute("combobox-input-error", Boolean(message));

  const error = combobox.querySelector("[data-combobox-error]");
  if (error instanceof HTMLElement) {
    error.textContent = message || error.dataset.defaultError || "";
    error.hidden = !message;
  }

  const trigger = combobox.querySelector("[data-combobox-trigger]");
  if (trigger instanceof HTMLButtonElement) {
    if (message) {
      trigger.setAttribute("aria-invalid", "true");
    } else {
      trigger.removeAttribute("aria-invalid");
    }
  }
}

/**
 * @param {HTMLElement} combobox
 */
function clearComboboxError(combobox) {
  setComboboxError(combobox, "");
}

const FUT_CARD_STEP_FIELD_ROOT_SELECTOR = [
  "fut-card-line-item-text",
  "fut-card-image-options",
  "product-file-upload",
  "fut-card-player-image-upload",
  "fut-card-player-position",
  ".fut-card-nationality",
  "fut-card-stats",
  "product-textarea-input",
].join(", ");

/**
 * @param {HTMLElement} stepBlock
 */
function clearFutCardStepValidationErrors(stepBlock) {
  stepBlock
    .querySelectorAll("input[data-fut-card-line-item-text]")
    .forEach((element) => {
      if (element instanceof HTMLInputElement) {
        element.setCustomValidity("");
        setFutCardLineItemTextError(element, "");
      }
    });

  stepBlock
    .querySelectorAll("input[data-fut-card-image-options-property]")
    .forEach((element) => {
      if (element instanceof HTMLInputElement) {
        clearFutCardImageOptionsError(element);
      }
    });

  stepBlock
    .querySelectorAll("input[data-fut-card-player-position]")
    .forEach((element) => {
      if (element instanceof HTMLInputElement) {
        clearFutCardPlayerPositionError(element);
      }
    });

  stepBlock.querySelectorAll("fut-card-stats").forEach((statsBlock) => {
    setFutCardStatsError(statsBlock, "");
    statsBlock.querySelectorAll("input, textarea").forEach((element) => {
      if (
        element instanceof HTMLInputElement ||
        element instanceof HTMLTextAreaElement
      ) {
        element.setCustomValidity("");
      }
    });
  });

  stepBlock
    .querySelectorAll("product-file-upload, fut-card-player-image-upload")
    .forEach((upload) => {
      const fileInput = upload.querySelector("input[type='file']");
      if (fileInput instanceof HTMLInputElement) {
        fileInput.setCustomValidity("");
      }

      const fileError = upload.querySelector(".product-file-upload__error");
      if (fileError instanceof HTMLElement) {
        fileError.hidden = true;
      }
    });

  stepBlock.querySelectorAll("theme-combobox").forEach((combobox) => {
    if (combobox instanceof HTMLElement) {
      clearComboboxError(combobox);
    }
  });

  stepBlock
    .querySelectorAll("textarea[data-product-textarea-input]")
    .forEach((element) => {
      if (element instanceof HTMLTextAreaElement) {
        element.setCustomValidity("");
        setProductTextareaError(element, "");
      }
    });
}

/**
 * @param {HTMLElement} fieldRoot
 * @returns {HTMLElement | null}
 */
function validateFutCardStepFieldRoot(fieldRoot) {
  if (fieldRoot.matches("fut-card-line-item-text")) {
    const input = fieldRoot.querySelector(
      "input[data-fut-card-line-item-text]",
    );
    if (!(input instanceof HTMLInputElement)) return null;

    const normalizedValue = input.value.trim();
    input.value = normalizedValue;

    if (input.required && !normalizedValue) {
      const errorMessage = getFutCardLineItemTextErrorMessage(input);
      input.setCustomValidity(errorMessage);
      setFutCardLineItemTextError(input, errorMessage);
      return input;
    }

    return null;
  }

  if (fieldRoot.matches("fut-card-image-options")) {
    const input = fieldRoot.querySelector(
      "input[data-fut-card-image-options-property]",
    );
    if (!(input instanceof HTMLInputElement)) return null;

    const errorMessage = validateFutCardImageOptionsInput(input);
    if (!errorMessage) return null;

    return fieldRoot.querySelector(".fut-card-image-options__grid") ?? input;
  }

  if (
    fieldRoot.matches("product-file-upload") ||
    fieldRoot.matches("fut-card-player-image-upload")
  ) {
    const input = fieldRoot.querySelector("input[type='file']");
    if (!(input instanceof HTMLInputElement)) return null;

    if (input.required && !input.files?.length) {
      const message =
        input.dataset.requiredMessage || FILE_UPLOAD_DEFAULT_MESSAGES.required;
      input.setCustomValidity(message);
      return input;
    }

    return null;
  }

  if (fieldRoot.matches("fut-card-player-position")) {
    const input = fieldRoot.querySelector(
      "input[data-fut-card-player-position]",
    );
    if (!(input instanceof HTMLInputElement)) return null;

    const errorMessage = validateFutCardPlayerPositionInput(input);
    if (!errorMessage) return null;

    return input;
  }

  if (fieldRoot.classList.contains("fut-card-nationality")) {
    const combobox = fieldRoot.querySelector("theme-combobox");
    if (!(combobox instanceof HTMLElement)) return null;

    const valueInput = combobox.querySelector("[data-combobox-value]");
    if (!(valueInput instanceof HTMLInputElement) || !valueInput.required) {
      return null;
    }

    if (!valueInput.value.trim()) {
      setComboboxError(combobox, getComboboxErrorMessage(combobox));
      const trigger = combobox.querySelector("[data-combobox-trigger]");
      if (trigger instanceof HTMLElement) return trigger;
      return combobox;
    }

    return null;
  }

  if (fieldRoot.matches("fut-card-stats")) {
    const errorMessage = validateFutCardStatsInputs(fieldRoot);
    if (!errorMessage) return null;

    const invalidControl =
      fieldRoot.querySelector(":invalid") ??
      fieldRoot.querySelector("[data-fut-card-stat-value]");

    return invalidControl instanceof HTMLElement ? invalidControl : fieldRoot;
  }

  if (fieldRoot.matches("product-textarea-input")) {
    const textarea = fieldRoot.querySelector(
      "textarea[data-product-textarea-input]",
    );
    if (!(textarea instanceof HTMLTextAreaElement)) return null;

    const normalizedValue = textarea.value
      .replace(/\r\n?/g, "\n")
      .replace(/\n(?:[ \t]*\n){2,}/g, "\n\n")
      .trim();
    textarea.value = normalizedValue;

    if (textarea.required && !normalizedValue) {
      const errorMessage = getProductTextareaErrorMessage(textarea);
      textarea.setCustomValidity(errorMessage);
      setProductTextareaError(textarea, errorMessage);
      return textarea;
    }
  }

  return null;
}

/**
 * @param {HTMLElement} stepBlock
 * @returns {HTMLElement | null}
 */
function getFutCardStepInvalidControl(stepBlock) {
  clearFutCardStepValidationErrors(stepBlock);

  for (const fieldRoot of stepBlock.querySelectorAll(
    FUT_CARD_STEP_FIELD_ROOT_SELECTOR,
  )) {
    if (!(fieldRoot instanceof HTMLElement)) continue;

    const invalidControl = validateFutCardStepFieldRoot(fieldRoot);
    if (invalidControl instanceof HTMLElement) return invalidControl;
  }

  return null;
}

/**
 * Validates required inputs in a personalization step before advancing.
 * @param {HTMLElement} stepBlock
 * @returns {boolean}
 */
export function validateFutCardPersonalizationStep(stepBlock) {
  if (!(stepBlock instanceof HTMLElement)) return true;

  const invalidControl = getFutCardStepInvalidControl(stepBlock);
  if (!invalidControl) return true;

  if (
    invalidControl instanceof HTMLInputElement ||
    invalidControl instanceof HTMLSelectElement ||
    invalidControl instanceof HTMLTextAreaElement
  ) {
    invalidControl.reportValidity();
  } else {
    invalidControl.focus();
    invalidControl.scrollIntoView({ block: "center", behavior: "smooth" });
  }

  return false;
}

/**
 * @param {HTMLTextAreaElement} textarea
 * @returns {string}
 */
function getProductTextareaErrorMessage(textarea) {
  if (textarea.dataset.requiredMessage) {
    return textarea.dataset.requiredMessage;
  }

  const error = textarea
    .closest("product-textarea-input")
    ?.querySelector("[data-product-textarea-error]");

  if (error instanceof HTMLElement && error.dataset.defaultError) {
    return error.dataset.defaultError;
  }

  return PRODUCT_TEXTAREA_REQUIRED_MESSAGE;
}

/**
 * @param {HTMLTextAreaElement} textarea
 * @param {string} message
 */
function setProductTextareaError(textarea, message) {
  const container = textarea.closest("product-textarea-input");
  if (!(container instanceof HTMLElement)) return;

  const error = container.querySelector("[data-product-textarea-error]");
  container.toggleAttribute("textarea-error", Boolean(message));

  if (!(error instanceof HTMLElement)) return;

  error.textContent = message || error.dataset.defaultError || "";
  error.hidden = !message;
}

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ["addToCartButton"];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("pointerenter", this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(
        /** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId),
      );
    }
    this.removeEventListener("pointerenter", this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * @param {boolean} loading
   */
  setLoading(loading) {
    const { addToCartButton } = this.refs;

    addToCartButton.disabled = loading;
    addToCartButton.dataset.loading = loading ? "true" : "false";
    addToCartButton.setAttribute("aria-busy", loading ? "true" : "false");
  }

  /**
   * Plays the success animations after an item is added to the cart.
   */
  showSuccessAnimation() {
    const animationEnabled = this.dataset.addToCartAnimation === "true";
    if (animationEnabled && !this.closest(".quick-add-modal")) {
      this.#animateFlyToCart();
    }
    this.animateAddToCart();
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest("form");
    if (form) normalizeProductLineItemInputs(form);
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before showing loading state
    const productForm = /** @type {ProductFormComponent | null} */ (
      this.closest("product-form-component")
    );
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      if (!validation.canAdd) {
        return;
      }
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector(".header-actions__cart-icon");

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (
      document.createElement("fly-to-cart")
    );

    let flyToCartClass = addToCartButton.classList.contains("quick-add__button")
      ? "fly-to-cart--quick"
      : "fly-to-cart--main";

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty("background-image", `url(${image})`);
    flyToCartElement.style.setProperty("--start-opacity", "0");
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(
      /** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId),
    );
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== "true") {
      addToCartButton.dataset.added = "true";
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute("data-added");

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get("add-to-cart-component")) {
  customElements.define("add-to-cart-component", AddToCartComponent);
}

/**
 * Handles drag-and-drop file selection for product line item uploads.
 *
 * @typedef {object} ProductFileUploadRefs
 * @property {HTMLInputElement} fileInput
 * @property {HTMLInputElement} fileNameInput
 * @property {HTMLElement | undefined} fileCount
 * @property {HTMLElement | undefined} fileLimitFeedback
 * @property {HTMLElement} fileLabel
 * @property {HTMLElement} dropZone
 * @property {HTMLElement} fileError
 * @property {HTMLElement} filePreview
 * @property {HTMLButtonElement | undefined} filePreviewButton
 * @property {HTMLElement | undefined} filePreviewIcon
 * @property {HTMLImageElement | undefined} filePreviewImage
 * @property {HTMLElement | undefined} filePreviewGrid
 * @property {HTMLElement | undefined} filePreviewName
 * @property {HTMLElement | undefined} filePreviewMeta
 * @property {HTMLDialogElement} filePreviewDialog
 * @property {HTMLImageElement} filePreviewDialogImage
 * @property {HTMLElement | undefined} filePreviewUnavailable
 * @property {HTMLAnchorElement | undefined} filePreviewDownload
 * @property {HTMLElement | undefined} filePreviewUnavailableDescription
 * @extends Component<ProductFileUploadRefs>
 */
export class ProductFileUpload extends Component {
  /** @type {ResizeObserver | undefined} */
  #variantPillsObserver;
  /** @type {AbortController | undefined} */
  #dragAbortController;
  /** @type {number} */
  #dragDepth = 0;
  /** @type {string | undefined} */
  #previewUrl;
  /** @type {string[]} */
  #previewUrls = [];
  /** @type {File | undefined} */
  #previewFile;
  /** @type {File[]} */
  #selectedFiles = [];
  /** @type {number} */
  #validationToken = 0;
  /** @type {number | undefined} */
  #feedbackTimeout;

  connectedCallback() {
    super.connectedCallback();
    this.refs.fileError.dataset.defaultError =
      this.refs.fileError.textContent.trim();
    this.#updateFileCount(0);
    this.#bindDropZoneEvents();
    this.#matchVariantPillsWidth();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#dragAbortController?.abort();
    this.#variantPillsObserver?.disconnect();
    if (this.#feedbackTimeout) clearTimeout(this.#feedbackTimeout);
    this.#revokePreviewUrls();
  }

  async handleFileChange() {
    const files = Array.from(this.refs.fileInput.files || []);
    if (files.length === 0) {
      if (this.refs.fileInput.multiple && this.#selectedFiles.length > 0) {
        this.#syncFileInputFiles(this.#selectedFiles);
        return;
      }

      this.#clearSelectedFile(true);
      return;
    }

    await this.#handleFilesSelected(files);
  }

  handleRemoveFile() {
    this.#validationToken += 1;
    this.refs.fileInput.value = "";
    this.#clearSelectedFile(true);
  }

  reset() {
    this.#validationToken += 1;
    this.refs.fileInput.value = "";
    this.#clearSelectedFile(true);
  }

  /** @param {KeyboardEvent} event */
  handleDropZoneKeyDown(event) {
    if (event.key !== "Enter" && event.key !== " ") return;

    event.preventDefault();
    this.refs.fileInput.click();
  }

  handleOpenPreview() {
    if (!this.#previewUrl) return;

    const previewFile = this.#previewFile || this.#selectedFiles[0];
    const canPreview = previewFile ? this.#canPreviewFile(previewFile) : true;

    this.refs.filePreviewDialog.classList.toggle(
      "product-file-upload__dialog--unavailable",
      !canPreview,
    );
    this.refs.filePreviewDialogImage.hidden = !canPreview;
    if (this.refs.filePreviewUnavailable) {
      this.refs.filePreviewUnavailable.hidden = canPreview;
    }

    if (canPreview) {
      this.refs.filePreviewDialogImage.src = this.#previewUrl;
    } else {
      this.refs.filePreviewDialogImage.removeAttribute("src");
      if (this.refs.filePreviewDownload) {
        this.refs.filePreviewDownload.href = this.#previewUrl;
        this.refs.filePreviewDownload.download = previewFile
          ? this.#sanitizeFileName(previewFile.name)
          : "file";
      }
      if (this.refs.filePreviewUnavailableDescription) {
        const fileName = previewFile
          ? this.#sanitizeFileName(previewFile.name)
          : "this file";
        const template =
          this.refs.filePreviewUnavailableDescription.dataset.template ||
          "preview for [filename] is unavailable.";
        this.#renderUnavailableDescription(template, fileName);
      }
    }

    if (this.refs.filePreviewDialog.open) return;

    this.refs.filePreviewDialog.showModal();
  }

  handleClosePreview() {
    if (!this.refs.filePreviewDialog.open) return;

    this.refs.filePreviewDialog.close();
  }

  /** @param {MouseEvent} event */
  handlePreviewDialogClick(event) {
    if (event.target === this.refs.filePreviewDialog) {
      this.handleClosePreview();
    }
  }

  #bindDropZoneEvents() {
    const { dropZone } = this.refs;
    if (!dropZone) return;

    this.#dragAbortController?.abort();
    this.#dragAbortController = new AbortController();
    const { signal } = this.#dragAbortController;

    dropZone.addEventListener("dragenter", this.#handleDragEnter, { signal });
    dropZone.addEventListener("dragover", this.#handleDragOver, { signal });
    dropZone.addEventListener("dragleave", this.#handleDragLeave, { signal });
    dropZone.addEventListener("drop", this.#handleDrop, { signal });
    this.refs.filePreviewGrid?.addEventListener(
      "click",
      this.#handlePreviewGridClick,
      { signal },
    );
    this.refs.fileInput.addEventListener("invalid", this.#handleInvalid, {
      signal,
    });
  }

  /** @param {Event} event */
  #handleInvalid = (event) => {
    event.preventDefault();

    if (!this.refs.fileInput.files?.length) {
      this.#showUploadError(this.#getMessage("required"));
    }
  };

  /** @param {DragEvent} event */
  #handleDragEnter = (event) => {
    if (!this.#eventHasFiles(event)) return;

    event.preventDefault();
    this.#dragDepth += 1;
    this.#setDragActive(true);
  };

  /** @param {DragEvent} event */
  #handleDragOver = (event) => {
    if (!this.#eventHasFiles(event)) return;

    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
    this.#setDragActive(true);
  };

  /** @param {DragEvent} event */
  #handleDragLeave = (event) => {
    if (!this.#eventHasFiles(event)) return;

    event.preventDefault();
    this.#dragDepth = Math.max(0, this.#dragDepth - 1);
    if (this.#dragDepth === 0) this.#setDragActive(false);
  };

  /** @param {DragEvent} event */
  #handleDrop = async (event) => {
    event.preventDefault();
    this.#dragDepth = 0;
    this.#setDragActive(false);

    const droppedFiles = Array.from(event.dataTransfer?.files || []);
    const files = this.refs.fileInput.multiple
      ? droppedFiles
      : droppedFiles.slice(0, 1);
    await this.#handleFilesSelected(files);
  };

  /**
   * @param {MouseEvent} event
   */
  #handlePreviewGridClick = (event) => {
    const target = /** @type {HTMLElement | null} */ (
      event.target instanceof HTMLElement ? event.target : null
    );

    const removeButton = target?.closest("[data-remove-index]");
    if (removeButton instanceof HTMLElement) {
      const removeIndex = Number(removeButton.dataset.removeIndex);
      this.#removeFileAtIndex(removeIndex);
      return;
    }

    const previewButton = target?.closest("[data-preview-index]");
    if (!(previewButton instanceof HTMLElement)) return;

    const previewIndex = Number(previewButton.dataset.previewIndex);
    const previewUrl = this.#previewUrls[previewIndex];
    if (!previewUrl) return;

    this.#previewUrl = previewUrl;
    this.#previewFile = this.#selectedFiles[previewIndex];
    this.handleOpenPreview();
  };

  /**
   * @param {File[]} selectedFiles
   */
  async #handleFilesSelected(selectedFiles) {
    if (selectedFiles.length === 0) return;

    if (this.refs.fileInput.multiple) {
      await this.#handleMultipleFilesSelected(selectedFiles);
      return;
    }

    const nextFiles = this.refs.fileInput.multiple
      ? [...this.#selectedFiles, ...selectedFiles]
      : selectedFiles;
    const { files, rejectedCount } = this.#limitFiles(nextFiles);
    if (files.length === 0) return;
    if (rejectedCount > 0) {
      this.#syncFileInputFiles(files);
      this.#showLimitFeedback(rejectedCount);
    }

    const validationToken = ++this.#validationToken;
    if (!(await this.#validateFiles(files, validationToken))) return;
    if (validationToken !== this.#validationToken) return;

    this.#syncFileInputFiles(files);
    this.#updateSelectedFiles(files);
  }

  /** @param {File[]} selectedFiles */
  async #handleMultipleFilesSelected(selectedFiles) {
    const maxFiles = this.#getMaxFiles();
    const availableSlots = Math.max(0, maxFiles - this.#selectedFiles.length);
    const candidates = selectedFiles.slice(0, availableSlots);
    const rejectedCount = selectedFiles.length - candidates.length;
    const validationToken = ++this.#validationToken;
    const validFiles = [];
    let invalidCount = 0;

    for (const file of candidates) {
      const validationError = this.#getFileValidationError(file);
      if (validationError) {
        invalidCount += 1;
        continue;
      }

      if (!(await this.#isValidImageFile(file, validationToken))) {
        invalidCount += 1;
        continue;
      }

      if (validationToken !== this.#validationToken) return;
      validFiles.push(file);
    }

    if (validationToken !== this.#validationToken) return;

    const files = [...this.#selectedFiles, ...validFiles];
    if (files.length === 0) {
      if (invalidCount > 0) this.#showInvalidFilesFeedback(invalidCount);
      return;
    }

    this.#syncFileInputFiles(files);
    this.#updateSelectedFiles(files);
    if (rejectedCount > 0) this.#showLimitFeedback(rejectedCount);
    if (invalidCount > 0) this.#showInvalidFilesFeedback(invalidCount);
  }

  /**
   * @param {File[]} files
   * @param {number} validationToken
   * @returns {Promise<boolean>}
   */
  async #validateFiles(files, validationToken) {
    for (const file of files) {
      if (!this.#validateFile(file)) return false;
      if (!(await this.#validateImageFile(file, validationToken))) return false;
      if (validationToken !== this.#validationToken) return false;
    }

    return true;
  }

  /** @param {File[]} files */
  #updateSelectedFiles(files) {
    const firstFile = files[0];
    if (!firstFile) return;

    this.refs.fileInput.setCustomValidity("");
    this.#selectedFiles = [...files];
    this.#hideUploadError();
    this.refs.fileLabel.textContent = this.#formatFileSelectionLabel(files);
    this.refs.fileNameInput.value = files
      .map((file) => this.#sanitizeFileName(file.name))
      .join(", ");
    if (this.refs.filePreviewName) {
      this.refs.filePreviewName.textContent =
        this.#formatFileSelectionLabel(files);
    }
    if (this.refs.filePreviewMeta) {
      this.refs.filePreviewMeta.textContent =
        this.#formatFileSelectionMeta(files);
    }
    this.#updateFileCount(files.length);
    this.refs.filePreview.hidden = false;
    this.toggleAttribute("file-selected", true);
    this.toggleAttribute(
      "max-files-reached",
      this.refs.fileInput.multiple && files.length >= this.#getMaxFiles(),
    );

    this.#revokePreviewUrls();
    this.#previewUrls = files.map((file) => URL.createObjectURL(file));
    this.#previewUrl = this.#previewUrls[0];
    this.#previewFile = firstFile;

    if (this.refs.filePreviewGrid) {
      this.#renderPreviewGrid(files);
    } else if (this.#previewUrl) {
      const canPreview = this.#canPreviewFile(firstFile);
      if (this.refs.filePreviewButton) {
        this.refs.filePreviewButton.hidden = !canPreview;
      }
      if (this.refs.filePreviewIcon) {
        this.refs.filePreviewIcon.hidden = canPreview;
        this.refs.filePreviewIcon.setAttribute(
          "aria-label",
          `preview ${firstFile.name}`,
        );
        this.refs.filePreviewIcon.replaceChildren(
          this.#createFallbackPreviewIcon(firstFile),
        );
      }
      if (this.refs.filePreviewImage) {
        if (canPreview) {
          this.refs.filePreviewImage.src = this.#previewUrl;
        } else {
          this.refs.filePreviewImage.removeAttribute("src");
        }
      }
    }

    this.dispatchEvent(
      new CustomEvent("product-file-upload:preview-change", { bubbles: true }),
    );
  }

  /** @param {boolean} clearValidity */
  #clearSelectedFile(clearValidity) {
    if (clearValidity) this.refs.fileInput.setCustomValidity("");
    if (clearValidity) this.#hideUploadError();
    this.refs.fileLabel.textContent =
      this.refs.fileLabel.dataset.emptyLabel || "";
    this.refs.fileNameInput.value = "";
    this.#selectedFiles = [];
    this.refs.filePreview.hidden = true;
    if (this.refs.filePreviewButton) this.refs.filePreviewButton.hidden = false;
    if (this.refs.filePreviewIcon) {
      this.refs.filePreviewIcon.hidden = true;
    }
    this.refs.filePreviewImage?.removeAttribute("src");
    this.refs.filePreviewDialogImage.removeAttribute("src");
    this.refs.filePreviewDialogImage.hidden = false;
    this.refs.filePreviewDialog.classList.remove(
      "product-file-upload__dialog--unavailable",
    );
    if (this.refs.filePreviewUnavailable) {
      this.refs.filePreviewUnavailable.hidden = true;
    }
    if (this.refs.filePreviewDownload) {
      this.refs.filePreviewDownload.removeAttribute("href");
      this.refs.filePreviewDownload.removeAttribute("download");
    }
    if (this.refs.filePreviewUnavailableDescription) {
      this.refs.filePreviewUnavailableDescription.textContent = "";
    }
    this.refs.filePreviewGrid?.replaceChildren();
    this.handleClosePreview();
    if (this.refs.filePreviewName) this.refs.filePreviewName.textContent = "";
    if (this.refs.filePreviewMeta) this.refs.filePreviewMeta.textContent = "";
    this.#updateFileCount(0);
    this.toggleAttribute("file-selected", false);
    this.toggleAttribute("max-files-reached", false);
    this.#revokePreviewUrls();

    this.dispatchEvent(
      new CustomEvent("product-file-upload:preview-change", { bubbles: true }),
    );
  }

  /** @param {boolean} isActive */
  #setDragActive(isActive) {
    this.toggleAttribute("drag-active", isActive);
  }

  /** @param {string} [message] */
  #showUploadError(message) {
    this.toggleAttribute("upload-error", true);
    this.refs.fileError.textContent =
      message || this.refs.fileError.dataset.defaultError || "";
    this.refs.fileError.hidden = false;
    this.refs.dropZone.scrollIntoView({ block: "center", behavior: "smooth" });
  }

  #hideUploadError() {
    this.toggleAttribute("upload-error", false);
    this.refs.fileError.hidden = true;
  }

  /** @param {number} rejectedCount */
  #showLimitFeedback(rejectedCount) {
    const feedback = this.refs.fileLimitFeedback;
    if (!feedback) return;

    const maxFiles = this.#getMaxFiles();
    feedback.textContent = this.#getMessage(
      rejectedCount === 1 ? "limitOne" : "limitMany",
      { count: rejectedCount, max: maxFiles },
    );
    feedback.hidden = false;

    if (this.#feedbackTimeout) clearTimeout(this.#feedbackTimeout);
    this.#feedbackTimeout = setTimeout(() => {
      feedback.hidden = true;
      feedback.textContent = "";
      this.#feedbackTimeout = undefined;
    }, FILE_UPLOAD_FEEDBACK_DURATION);
  }

  /**
   * @param {DragEvent} event
   * @returns {boolean}
   */
  #eventHasFiles(event) {
    return Array.from(event.dataTransfer?.types || []).includes("Files");
  }

  #matchVariantPillsWidth() {
    const root =
      this.closest(".fut-card-personalization-popup") ??
      this.closest(".product-details");
    const variantPills = root?.querySelector(".variant-option--buttons");

    if (!(variantPills instanceof HTMLElement)) {
      this.style.removeProperty("max-width");
      return;
    }

    const updateWidth = () => {
      const width = variantPills.offsetWidth;

      if (width <= 0) {
        this.style.removeProperty("max-width");
        return;
      }

      this.style.maxWidth = `${width}px`;
    };

    updateWidth();
    this.#variantPillsObserver?.disconnect();
    this.#variantPillsObserver = new ResizeObserver(updateWidth);
    this.#variantPillsObserver.observe(variantPills);
  }

  /** @returns {File[]} */
  #getSelectedFiles() {
    if (this.refs.fileInput.multiple) return [...this.#selectedFiles];

    return Array.from(this.refs.fileInput.files || []).slice(0, 1);
  }

  /** @returns {number} */
  #getMaxFiles() {
    if (!this.refs.fileInput.multiple) return 1;

    const maxFiles = Number(this.refs.fileInput.dataset.maxFiles);
    return Number.isFinite(maxFiles) && maxFiles > 0
      ? Math.floor(maxFiles)
      : Infinity;
  }

  /**
   * @param {keyof typeof FILE_UPLOAD_DEFAULT_MESSAGES} key
   * @param {Record<string, string | number>} values
   * @returns {string}
   */
  #getMessage(key, values = {}) {
    const template =
      this.refs.fileInput.dataset[`${key}Message`] ||
      FILE_UPLOAD_DEFAULT_MESSAGES[key] ||
      "";

    return formatMessageTemplate(template, values);
  }

  /** @param {File[]} files */
  #limitFiles(files) {
    const maxFiles = this.#getMaxFiles();
    if (files.length <= maxFiles) return { files, rejectedCount: 0 };

    return {
      files: files.slice(0, maxFiles),
      rejectedCount: files.length - maxFiles,
    };
  }

  /** @param {File[]} files */
  #syncFileInputFiles(files) {
    const dataTransfer = new DataTransfer();
    for (const file of files) dataTransfer.items.add(file);
    this.refs.fileInput.files = dataTransfer.files;
  }

  /** @param {number} index */
  #removeFileAtIndex(index) {
    if (!Number.isInteger(index)) return;

    const files = this.#getSelectedFiles();
    if (index < 0 || index >= files.length) return;

    files.splice(index, 1);
    this.#validationToken += 1;

    if (files.length === 0) {
      this.refs.fileInput.value = "";
      this.#clearSelectedFile(true);
      return;
    }

    this.#syncFileInputFiles(files);
    this.#updateSelectedFiles(files);
  }

  /** @param {number} count */
  #updateFileCount(count) {
    const fileCount = this.refs.fileCount;
    if (!fileCount) return;

    const maxFiles =
      fileCount.dataset.maxFiles || this.refs.fileInput.dataset.maxFiles || "1";
    fileCount.textContent = this.#getMessage("fileCount", {
      count,
      max: maxFiles,
    });
  }

  /**
   * @param {File} file
   * @returns {boolean}
   */
  #validateFile(file) {
    const validationError = this.#getFileValidationError(file);
    if (validationError) {
      this.#showFileError(validationError);
      return false;
    }

    return true;
  }

  /** @param {File} file */
  #getFileValidationError(file) {
    const maxSize = Number(this.refs.fileInput.dataset.maxFileSize) || 0;
    const maxSizeMb = this.refs.fileInput.dataset.maxFileSizeMb || "";

    if (file.size <= 0) return this.#getMessage("invalid");

    if (BLOCKED_UPLOAD_EXTENSIONS.has(this.#getFileExtension(file.name))) {
      return this.#getMessage("unsupported");
    }

    if (maxSize > 0 && file.size > maxSize) {
      return this.#getMessage("size", {
        max_size: maxSizeMb,
        max_size_mb: maxSizeMb,
      });
    }

    if (!this.#isAcceptedFile(file)) {
      return this.#getMessage("unsupported");
    }

    return "";
  }

  /**
   * @param {File} file
   * @returns {boolean}
   */
  #isAcceptedFile(file) {
    const accept = this.refs.fileInput.accept;
    if (!accept) return true;

    return accept.split(",").some((acceptedType) => {
      const type = acceptedType.trim();
      if (!type) return false;
      const extension = this.#getFileExtension(file.name);
      if (type.startsWith(".")) return extension === type.toLowerCase();
      if (type.endsWith("/*"))
        return file.type.startsWith(type.replace("/*", "/"));
      if (file.type === type) return true;
      return MIME_TYPE_EXTENSIONS.get(type)?.includes(extension) || false;
    });
  }

  /**
   * @param {File} file
   * @param {number} validationToken
   * @returns {Promise<boolean>}
   */
  async #validateImageFile(file, validationToken) {
    if (!this.#shouldValidateAsImage(file)) return true;

    this.refs.fileInput.setCustomValidity(this.#getMessage("validating"));

    const url = URL.createObjectURL(file);
    const isValidImage = await new Promise((resolve) => {
      const image = new Image();
      image.onload = () => resolve(true);
      image.onerror = () => resolve(false);
      image.src = url;
    });

    URL.revokeObjectURL(url);

    if (validationToken !== this.#validationToken) return false;

    if (!isValidImage) {
      this.#showFileError(this.#getMessage("invalid"));
      return false;
    }

    this.refs.fileInput.setCustomValidity("");
    return true;
  }

  /**
   * @param {File} file
   * @param {number} validationToken
   * @returns {Promise<boolean>}
   */
  async #isValidImageFile(file, validationToken) {
    if (!this.#shouldValidateAsImage(file)) return true;

    const url = URL.createObjectURL(file);
    const isValidImage = await new Promise((resolve) => {
      const image = new Image();
      image.onload = () => resolve(true);
      image.onerror = () => resolve(false);
      image.src = url;
    });

    URL.revokeObjectURL(url);

    return validationToken === this.#validationToken && Boolean(isValidImage);
  }

  /**
   * @param {File} file
   * @returns {boolean}
   */
  #shouldValidateAsImage(file) {
    if (
      SKIP_IMAGE_DECODE_VALIDATION_EXTENSIONS.has(
        this.#getFileExtension(file.name),
      )
    )
      return false;
    if (file.type.startsWith("image/")) return true;
    return IMAGE_UPLOAD_EXTENSIONS.has(this.#getFileExtension(file.name));
  }

  /** @param {File} file */
  #isImageFile(file) {
    if (file.type.startsWith("image/")) return true;
    return IMAGE_UPLOAD_EXTENSIONS.has(this.#getFileExtension(file.name));
  }

  /**
   * @param {File} file
   * @returns {boolean}
   */
  #canPreviewFile(file) {
    if (
      SKIP_IMAGE_DECODE_VALIDATION_EXTENSIONS.has(
        this.#getFileExtension(file.name),
      )
    )
      return false;

    return this.#shouldValidateAsImage(file);
  }

  /**
   * @param {string} fileName
   * @returns {string}
   */
  #getFileExtension(fileName) {
    const extensionStart = fileName.lastIndexOf(".");
    if (extensionStart < 0) return "";
    return fileName.slice(extensionStart).toLowerCase();
  }

  /**
   * @param {string} fileName
   * @returns {string}
   */
  #sanitizeFileName(fileName) {
    return fileName
      .replace(/[\r\n\t]/g, " ")
      .trim()
      .slice(0, UPLOAD_FILE_NAME_MAX_LENGTH);
  }

  /** @param {string} message */
  #showFileError(message) {
    if (this.refs.fileInput.multiple && this.#selectedFiles.length > 0) {
      this.refs.fileInput.setCustomValidity(message);
      this.refs.fileInput.reportValidity();
      this.#syncFileInputFiles(this.#selectedFiles);
      this.#updateSelectedFiles(this.#selectedFiles);
      this.#showUploadError(message);
      return;
    }

    this.refs.fileInput.value = "";
    this.refs.fileInput.setCustomValidity(message);
    this.refs.fileInput.reportValidity();
    this.#clearSelectedFile(false);
    this.#showUploadError(message);
  }

  /** @param {number} invalidCount */
  #showInvalidFilesFeedback(invalidCount) {
    this.#showUploadError(
      this.#getMessage(
        invalidCount === 1 ? "invalidFilesOne" : "invalidFilesMany",
        { count: invalidCount },
      ),
    );
  }

  /** @param {number} bytes */
  #formatFileSize(bytes) {
    if (bytes < 1024 * 1024)
      return `${Math.max(1, Math.round(bytes / 1024))} kb`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} mb`;
  }

  /** @param {File[]} files */
  #formatFileSelectionLabel(files) {
    const firstFile = files[0];
    if (files.length === 1 && firstFile) return firstFile.name;

    return this.#getMessage("filesSelected", { count: files.length });
  }

  /** @param {File[]} files */
  #formatFileSelectionMeta(files) {
    if (files.length === 1 && files[0])
      return this.#formatFileSize(files[0].size);

    const totalSize = files.reduce((total, file) => total + file.size, 0);
    return `${this.#formatFileSize(totalSize)} total`;
  }

  /** @param {File[]} files */
  #renderPreviewGrid(files) {
    const grid = this.refs.filePreviewGrid;
    if (!grid) return;

    grid.replaceChildren();
    files.forEach((file, index) => {
      const previewUrl = this.#previewUrls[index];
      if (!previewUrl) return;

      const card = document.createElement("div");
      card.className = "product-file-upload__preview-card";

      const removeButton = document.createElement("button");
      removeButton.type = "button";
      removeButton.className = "product-file-upload__remove";
      removeButton.dataset.removeIndex = index.toString();
      removeButton.setAttribute("aria-label", `remove ${file.name}`);
      removeButton.textContent = "×";

      const preview = this.#canPreviewFile(file)
        ? this.#createPreviewButton(file, previewUrl, index)
        : this.#createFileIcon(file, index);

      const details = document.createElement("div");
      details.className = "product-file-upload__preview-details";

      const name = document.createElement("span");
      name.className = "product-file-upload__preview-name";
      name.textContent = file.name;

      const meta = document.createElement("span");
      meta.className = "product-file-upload__preview-meta";
      meta.textContent = this.#formatFileSize(file.size);

      details.append(name, meta);
      card.append(removeButton, preview, details);
      grid.appendChild(card);
    });
  }

  /**
   * @param {File} file
   * @param {string} previewUrl
   * @param {number} index
   * @returns {HTMLButtonElement}
   */
  #createPreviewButton(file, previewUrl, index) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "product-file-upload__preview-grid-button";
    button.dataset.previewIndex = index.toString();
    button.setAttribute("aria-label", `preview ${file.name}`);

    const image = document.createElement("img");
    image.className = "product-file-upload__preview-grid-image";
    image.src = previewUrl;
    image.alt = "";
    image.width = 120;
    image.height = 120;

    button.appendChild(image);
    return button;
  }

  /**
   * @param {File} file
   * @param {number} index
   * @returns {HTMLButtonElement}
   */
  #createFileIcon(file, index) {
    const icon = document.createElement("button");
    icon.type = "button";
    icon.className = "product-file-upload__preview-file-icon";
    icon.dataset.previewIndex = index.toString();
    icon.setAttribute("aria-label", `preview ${file.name}`);
    icon.appendChild(this.#createFallbackPreviewIcon(file));
    return icon;
  }

  /**
   * @param {File} file
   * @returns {SVGSVGElement}
   */
  #createFallbackPreviewIcon(file) {
    return this.#isImageFile(file)
      ? this.#createImageIconSvg()
      : this.#createFileIconSvg();
  }

  /** @returns {SVGSVGElement} */
  #createImageIconSvg() {
    const svg = this.#createBaseIconSvg(
      "lucide lucide-image-icon lucide-image",
    );

    const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
    rect.setAttribute("width", "18");
    rect.setAttribute("height", "18");
    rect.setAttribute("x", "3");
    rect.setAttribute("y", "3");
    rect.setAttribute("rx", "2");
    rect.setAttribute("ry", "2");

    const circle = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "circle",
    );
    circle.setAttribute("cx", "9");
    circle.setAttribute("cy", "9");
    circle.setAttribute("r", "2");

    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("d", "m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21");

    svg.append(rect, circle, path);
    return svg;
  }

  /** @returns {SVGSVGElement} */
  #createFileIconSvg() {
    const svg = this.#createBaseIconSvg("lucide lucide-file-icon lucide-file");

    const filePath = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "path",
    );
    filePath.setAttribute(
      "d",
      "M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z",
    );

    const foldPath = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "path",
    );
    foldPath.setAttribute("d", "M14 2v5a1 1 0 0 0 1 1h5");

    svg.append(filePath, foldPath);
    return svg;
  }

  /**
   * @param {string} className
   * @returns {SVGSVGElement}
   */
  #createBaseIconSvg(className) {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    svg.setAttribute("width", "24");
    svg.setAttribute("height", "24");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("fill", "none");
    svg.setAttribute("stroke", "currentColor");
    svg.setAttribute("stroke-width", "2");
    svg.setAttribute("stroke-linecap", "round");
    svg.setAttribute("stroke-linejoin", "round");
    svg.setAttribute("class", className);
    svg.setAttribute("aria-hidden", "true");
    return svg;
  }

  /**
   * @param {string} template
   * @param {string} fileName
   */
  #renderUnavailableDescription(template, fileName) {
    const description = this.refs.filePreviewUnavailableDescription;
    if (!description) return;

    const templateParts = template.split("[filename]");
    const beforeFileName = templateParts[0] || "";
    const afterFileName = templateParts.slice(1).join("[filename]");
    const fileNameElement = document.createElement("span");
    fileNameElement.className = "product-file-upload__dialog-file-name";
    fileNameElement.textContent = fileName;

    description.replaceChildren(
      document.createTextNode(beforeFileName),
      fileNameElement,
      document.createTextNode(afterFileName),
    );
  }

  #revokePreviewUrls() {
    const previewUrls = new Set(this.#previewUrls);
    if (this.#previewUrl) previewUrls.add(this.#previewUrl);

    for (const previewUrl of previewUrls) {
      URL.revokeObjectURL(previewUrl);
    }

    this.#previewUrls = [];
    this.#previewUrl = undefined;
    this.#previewFile = undefined;
  }
}

if (!customElements.get("product-file-upload")) {
  customElements.define("product-file-upload", ProductFileUpload);
}

/**
 * Handles inline validation for product textarea line item properties.
 *
 * @typedef {object} ProductTextareaInputRefs
 * @property {HTMLTextAreaElement} textareaInput
 * @property {HTMLElement} textareaError
 * @extends Component<ProductTextareaInputRefs>
 */
class ProductTextareaInput extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();
    this.refs.textareaError.dataset.defaultError =
      this.refs.textareaError.textContent.trim();

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.refs.textareaInput.addEventListener("invalid", this.#handleInvalid, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  handleInput() {
    const hasValue = Boolean(this.refs.textareaInput.value.trim());
    if (!hasValue && this.refs.textareaInput.required) return;

    this.refs.textareaInput.setCustomValidity("");
    setProductTextareaError(this.refs.textareaInput, "");
  }

  reset() {
    this.refs.textareaInput.value = "";
    this.refs.textareaInput.setCustomValidity("");
    setProductTextareaError(this.refs.textareaInput, "");
  }

  /** @param {Event} event */
  #handleInvalid = (event) => {
    event.preventDefault();
    setProductTextareaError(
      this.refs.textareaInput,
      getProductTextareaErrorMessage(this.refs.textareaInput),
    );
    this.refs.textareaInput.scrollIntoView({
      block: "center",
      behavior: "smooth",
    });
  };
}

if (!customElements.get("product-textarea-input")) {
  customElements.define("product-textarea-input", ProductTextareaInput);
}

/**
 * Handles inline validation for fut card single-line text line item properties.
 *
 * @typedef {object} FutCardLineItemTextRefs
 * @property {HTMLInputElement} textInput
 * @property {HTMLElement} textError
 * @extends Component<FutCardLineItemTextRefs>
 */
class FutCardLineItemText extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();
    this.refs.textError.dataset.defaultError =
      this.refs.textError.textContent.trim();

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.refs.textInput.addEventListener("invalid", this.#handleInvalid, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  handleInput() {
    const hasValue = Boolean(this.refs.textInput.value.trim());
    if (!hasValue && this.refs.textInput.required) return;

    this.refs.textInput.setCustomValidity("");
    setFutCardLineItemTextError(this.refs.textInput, "");
  }

  reset() {
    this.refs.textInput.value = "";
    this.refs.textInput.setCustomValidity("");
    setFutCardLineItemTextError(this.refs.textInput, "");
  }

  /** @param {Event} event */
  #handleInvalid = (event) => {
    event.preventDefault();
    setFutCardLineItemTextError(
      this.refs.textInput,
      getFutCardLineItemTextErrorMessage(this.refs.textInput),
    );
    this.refs.textInput.scrollIntoView({
      block: "center",
      behavior: "smooth",
    });
  };
}

if (!customElements.get("fut-card-line-item-text")) {
  customElements.define("fut-card-line-item-text", FutCardLineItemText);
}

/**
 * @param {ParentNode} container
 * @returns {HTMLElement | null}
 */
function getFutCardPlayerPositionStateRoot(container) {
  if (!(container instanceof HTMLElement)) return null;
  if (container.classList.contains("fut-card-player-position"))
    return container;

  const inner = container.querySelector(".fut-card-player-position");
  return inner instanceof HTMLElement ? inner : container;
}

/**
 * @param {ParentNode} container
 * @returns {HTMLElement | null}
 */
function getActiveFutCardPlayerPositionLanguagePanel(container) {
  const stateRoot = getFutCardPlayerPositionStateRoot(container);
  if (!stateRoot) return null;

  const activeLanguage = stateRoot.dataset.activeLanguage;
  if (activeLanguage) {
    const panel = stateRoot.querySelector(
      `[data-fut-card-position-lang-panel="${activeLanguage}"]`,
    );
    if (panel instanceof HTMLElement) return panel;
  }

  const activePanel = stateRoot.querySelector(
    "[data-fut-card-position-lang-panel].is-active",
  );
  if (activePanel instanceof HTMLElement) return activePanel;

  const visiblePanel = stateRoot.querySelector(
    "[data-fut-card-position-lang-panel]:not([hidden])",
  );
  return visiblePanel instanceof HTMLElement ? visiblePanel : null;
}

/**
 * @param {ParentNode} container
 * @returns {Set<string>}
 */
function getFutCardPlayerPositionPresets(container) {
  const presets = new Set();
  const panel =
    getActiveFutCardPlayerPositionLanguagePanel(container) ?? container;

  panel.querySelectorAll("[data-fut-card-position-preset]").forEach((input) => {
    if (!(input instanceof HTMLInputElement)) return;

    const position = input.value.trim().toUpperCase();
    if (position) presets.add(position);
  });

  return presets;
}

/**
 * Handles preset and custom position selection for fut card line item properties.
 *
 * @typedef {object} FutCardPlayerPositionRefs
 * @property {HTMLInputElement} propertyInput
 * @property {HTMLInputElement} customInput
 * @property {HTMLElement} positionError
 * @property {HTMLElement} presetGrid
 * @property {HTMLElement} customField
 * @extends Component<FutCardPlayerPositionRefs>
 */
class FutCardPlayerPosition extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {HTMLInputElement[]} */
  #presetInputs = [];

  connectedCallback() {
    super.connectedCallback();

    this.#presetInputs = /** @type {HTMLInputElement[]} */ (
      Array.from(this.querySelectorAll("[data-fut-card-position-preset]"))
    );

    const propertyInput = this.#getPropertyInput();
    const positionError = this.#getPositionError();
    const customInput = this.#getCustomInput();
    const customRadio = this.#getCustomRadio();

    if (positionError) {
      positionError.dataset.defaultError = positionError.textContent.trim();
    }

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    propertyInput?.addEventListener("invalid", this.#handleInvalid, { signal });

    for (const input of this.#presetInputs) {
      input.addEventListener("change", this.#handleOptionChange, { signal });
    }

    customRadio?.addEventListener("change", this.#handleOptionChange, {
      signal,
    });
    customInput?.addEventListener("input", this.#handleCustomInputBound, {
      signal,
    });

    this.#initializeLanguagePanels();
    this.#syncCustomRadioGroupName();
    this.#syncSelection(propertyInput?.value ?? "");
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  handleCustomInput() {
    const customInput = this.#getCustomInput();
    const propertyInput = this.#getPropertyInput();
    const customRadio = this.#getCustomRadio();
    if (
      !(customInput instanceof HTMLInputElement) ||
      !(propertyInput instanceof HTMLInputElement)
    ) {
      return;
    }

    if (customRadio instanceof HTMLInputElement) {
      customRadio.checked = true;
    }

    const customValue = customInput.value.trim().toUpperCase();
    customInput.value = customValue;
    this.#setPropertyValue(customValue);
    this.#syncSelection(customValue);

    if (!customValue) {
      clearFutCardPlayerPositionError(propertyInput);
      return;
    }

    validateFutCardPlayerPositionInput(propertyInput);
  }

  /** @param {Event} event */
  handleLanguageSelect(event) {
    const target = event.target;
    if (!(target instanceof HTMLButtonElement)) return;

    const lang = target.dataset.futCardPositionLang;
    const stateRoot = getFutCardPlayerPositionStateRoot(this);
    if (!lang || !stateRoot || lang === stateRoot.dataset.activeLanguage)
      return;

    this.#switchLanguage(lang);
  }

  /** @param {string} lang */
  #switchLanguage(lang) {
    const stateRoot = getFutCardPlayerPositionStateRoot(this);
    if (!stateRoot) return;

    this.#applyActiveLanguage(lang, { clearSelection: true });
  }

  #initializeLanguagePanels() {
    const stateRoot = getFutCardPlayerPositionStateRoot(this);
    if (!stateRoot) return;

    const activeLanguage = stateRoot.dataset.activeLanguage || "en";
    this.#applyActiveLanguage(activeLanguage);
  }

  /**
   * @param {string} lang
   * @param {{ clearSelection?: boolean }} [options]
   */
  #applyActiveLanguage(lang, options = {}) {
    const stateRoot = getFutCardPlayerPositionStateRoot(this);
    if (!stateRoot) return;

    const panels = stateRoot.querySelectorAll(
      "[data-fut-card-position-lang-panel]",
    );
    const tabs = stateRoot.querySelectorAll("[data-fut-card-position-lang]");

    for (const panel of panels) {
      if (!(panel instanceof HTMLElement)) continue;

      const isActive = panel.dataset.futCardPositionLangPanel === lang;
      panel.hidden = !isActive;
      panel.classList.toggle("is-active", isActive);
    }

    for (const tab of tabs) {
      if (!(tab instanceof HTMLButtonElement)) continue;
      tab.setAttribute(
        "aria-selected",
        tab.dataset.futCardPositionLang === lang ? "true" : "false",
      );
    }

    stateRoot.dataset.activeLanguage = lang;
    this.#syncCustomRadioGroupName();

    if (options.clearSelection) {
      this.#clearSelection();
    }
  }

  /** @returns {string | null} */
  #getActivePresetGroupName() {
    const activePanel = getActiveFutCardPlayerPositionLanguagePanel(this);
    const firstPreset = activePanel?.querySelector(
      "[data-fut-card-position-preset]",
    );

    return firstPreset instanceof HTMLInputElement ? firstPreset.name : null;
  }

  #syncCustomRadioGroupName() {
    const customRadio = this.#getCustomRadio();
    const groupName = this.#getActivePresetGroupName();

    if (customRadio instanceof HTMLInputElement && groupName) {
      customRadio.name = groupName;
    }
  }

  reset() {
    this.#clearSelection();
    this.#initializeLanguagePanels();
  }

  #clearSelection() {
    for (const input of this.querySelectorAll(
      "[data-fut-card-position-preset]",
    )) {
      if (!(input instanceof HTMLInputElement)) continue;

      input.checked = false;

      const label = input.closest("[data-fut-card-position-preset-label]");
      if (label instanceof HTMLElement) {
        label.dataset.selected = "false";
      }
    }

    const customRadio = this.#getCustomRadio();
    if (customRadio instanceof HTMLInputElement) {
      customRadio.checked = false;

      const customLabel = customRadio.closest(
        "[data-fut-card-position-preset-label]",
      );
      if (customLabel instanceof HTMLElement) {
        customLabel.dataset.selected = "false";
      }
    }

    const customInput = this.#getCustomInput();
    if (customInput instanceof HTMLInputElement) {
      customInput.value = "";
    }

    const customField = this.#getCustomField();
    if (customField instanceof HTMLElement) {
      customField.hidden = true;
    }

    const propertyInput = this.#getPropertyInput();
    if (propertyInput instanceof HTMLInputElement) {
      this.#setPropertyValue("");
      clearFutCardPlayerPositionError(propertyInput);
    }
  }

  /** @returns {HTMLInputElement | null} */
  #getPropertyInput() {
    const ref = this.refs.propertyInput;
    if (ref instanceof HTMLInputElement) return ref;
    const found = this.querySelector("[data-fut-card-player-position]");
    return found instanceof HTMLInputElement ? found : null;
  }

  /** @returns {HTMLInputElement | null} */
  #getCustomInput() {
    const ref = this.refs.customInput;
    if (ref instanceof HTMLInputElement) return ref;
    const found = this.querySelector(".fut-card-player-position__custom-input");
    return found instanceof HTMLInputElement ? found : null;
  }

  /** @returns {HTMLInputElement | null} */
  #getCustomRadio() {
    const found = this.querySelector("[data-fut-card-position-custom]");
    return found instanceof HTMLInputElement ? found : null;
  }

  /** @returns {HTMLElement | null} */
  #getCustomField() {
    const ref = this.refs.customField;
    if (ref instanceof HTMLElement) return ref;
    const found = this.querySelector(".fut-card-player-position__custom-field");
    return found instanceof HTMLElement ? found : null;
  }

  /** @returns {HTMLElement | null} */
  #getPositionError() {
    const ref = this.refs.positionError;
    if (ref instanceof HTMLElement) return ref;
    const found = this.querySelector("[data-fut-card-player-position-error]");
    return found instanceof HTMLElement ? found : null;
  }

  /** @param {Event} event */
  #handleOptionChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.checked) return;

    const propertyInput = this.#getPropertyInput();
    if (!(propertyInput instanceof HTMLInputElement)) return;

    if (target.matches("[data-fut-card-position-custom]")) {
      this.#activateCustomMode();
      return;
    }

    const position = target.value.trim().toUpperCase();
    const presets = getFutCardPlayerPositionPresets(this);
    if (!position || !presets.has(position)) return;

    const customRadio = this.#getCustomRadio();
    if (customRadio instanceof HTMLInputElement) {
      customRadio.checked = false;
    }

    const customInput = this.#getCustomInput();
    if (customInput instanceof HTMLInputElement) {
      customInput.value = "";
    }

    this.#setPropertyValue(position);
    this.#syncSelection(position);
    clearFutCardPlayerPositionError(propertyInput);
    validateFutCardPlayerPositionInput(propertyInput);
  };

  #activateCustomMode() {
    const customInput = this.#getCustomInput();
    const customField = this.#getCustomField();
    const propertyInput = this.#getPropertyInput();

    for (const input of this.#presetInputs) {
      input.checked = false;
    }

    if (customField instanceof HTMLElement) {
      customField.hidden = false;
    }

    const customValue =
      customInput instanceof HTMLInputElement
        ? customInput.value.trim().toUpperCase()
        : "";

    if (propertyInput instanceof HTMLInputElement) {
      this.#setPropertyValue(customValue);
      clearFutCardPlayerPositionError(propertyInput);
    }

    this.#syncSelection(customValue);

    if (customInput instanceof HTMLInputElement) {
      customInput.focus();
    }
  }

  #handleCustomInputBound = () => {
    this.handleCustomInput();
  };

  /** @param {string} value */
  #setPropertyValue(value) {
    const propertyInput = this.#getPropertyInput();
    if (propertyInput instanceof HTMLInputElement) {
      propertyInput.value = value;
      propertyInput.dispatchEvent(new Event("input", { bubbles: true }));
    }
  }

  /** @param {string} value */
  #syncSelection(value) {
    const normalizedValue = value.trim().toUpperCase();
    const customRadio = this.#getCustomRadio();
    const customField = this.#getCustomField();
    const isCustomSelected = customRadio?.checked ?? false;
    const presets = getFutCardPlayerPositionPresets(this);
    const activePanel = getActiveFutCardPlayerPositionLanguagePanel(this);
    const presetInputs = activePanel
      ? activePanel.querySelectorAll("[data-fut-card-position-preset]")
      : this.querySelectorAll("[data-fut-card-position-preset]");

    for (const input of presetInputs) {
      if (!(input instanceof HTMLInputElement)) continue;

      const position = input.value.trim().toUpperCase();
      const isSelected =
        !isCustomSelected &&
        normalizedValue !== "" &&
        position === normalizedValue &&
        presets.has(position);

      input.checked = isSelected;

      const label = input.closest("[data-fut-card-position-preset-label]");
      if (label instanceof HTMLElement) {
        label.dataset.selected = isSelected ? "true" : "false";
      }
    }

    if (customRadio instanceof HTMLInputElement) {
      const customLabel = customRadio.closest(
        "[data-fut-card-position-preset-label]",
      );
      if (customLabel instanceof HTMLElement) {
        customLabel.dataset.selected = isCustomSelected ? "true" : "false";
      }
    }

    if (customField instanceof HTMLElement) {
      customField.hidden = !isCustomSelected;
    }
  }

  /** @param {Event} event */
  #handleInvalid = (event) => {
    event.preventDefault();

    const propertyInput = this.#getPropertyInput();
    if (!(propertyInput instanceof HTMLInputElement)) return;

    validateFutCardPlayerPositionInput(propertyInput);

    const customRadio = this.#getCustomRadio();
    const customInput = this.#getCustomInput();
    const focusTarget =
      customRadio?.checked && customInput instanceof HTMLInputElement
        ? customInput
        : (this.refs.presetGrid ??
          this.querySelector(".fut-card-player-position__preset-rows"));

    if (focusTarget instanceof HTMLElement) {
      focusTarget.scrollIntoView({
        block: "center",
        behavior: "smooth",
      });
    }
  };
}

if (!customElements.get("fut-card-player-position")) {
  customElements.define("fut-card-player-position", FutCardPlayerPosition);
}

/**
 * Handles image option selection for fut card line item properties.
 *
 * @typedef {object} FutCardImageOptionsRefs
 * @property {HTMLInputElement} propertyInput
 * @property {HTMLElement} optionsError
 * @property {HTMLElement} optionGrid
 * @extends Component<FutCardImageOptionsRefs>
 */
class FutCardImageOptions extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {HTMLInputElement[]} */
  #optionInputs = [];

  connectedCallback() {
    super.connectedCallback();

    this.#optionInputs = /** @type {HTMLInputElement[]} */ (
      Array.from(this.querySelectorAll("[data-fut-card-image-option]"))
    );

    this.#syncOptionGroupNames();

    const propertyInput = this.#getPropertyInput();
    const optionsError = this.#getOptionsError();

    if (optionsError) {
      optionsError.dataset.defaultError = optionsError.textContent.trim();
    }

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    propertyInput?.addEventListener("invalid", this.#handleInvalid, { signal });

    for (const input of this.#optionInputs) {
      input.addEventListener("change", this.#handleOptionChange, { signal });
    }

    this.#ensureDefaultSelection();
    this.#syncSelection(propertyInput?.value ?? "");
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /** @returns {HTMLInputElement | null} */
  #getPropertyInput() {
    const ref = this.refs.propertyInput;
    if (ref instanceof HTMLInputElement) return ref;
    const found = this.querySelector("[data-fut-card-image-options-property]");
    return found instanceof HTMLInputElement ? found : null;
  }

  #syncOptionGroupNames() {
    const stateRoot = this.querySelector(".fut-card-image-options");
    if (!(stateRoot instanceof HTMLElement)) return;

    const groupName = stateRoot.dataset.optionGroupName;
    if (!groupName) return;

    for (const input of this.#optionInputs) {
      input.name = groupName;
    }
  }

  /** @returns {HTMLElement | null} */
  #getOptionsError() {
    const ref = this.refs.optionsError;
    if (ref instanceof HTMLElement) return ref;
    const found = this.querySelector("[data-fut-card-image-options-error]");
    return found instanceof HTMLElement ? found : null;
  }

  /** @param {Event} event */
  #handleOptionChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.checked) return;

    this.#selectOption(target);
  };

  reset() {
    const propertyInput = this.#getPropertyInput();
    if (propertyInput instanceof HTMLInputElement) {
      this.#setPropertyValue("");
      clearFutCardImageOptionsError(propertyInput);
    }
    for (const input of this.#optionInputs) {
      input.checked = false;
    }
    const defaultOption =
      this.#optionInputs.find((input) => input.defaultChecked) ??
      this.#optionInputs[0];
    if (defaultOption instanceof HTMLInputElement) {
      this.#selectOption(defaultOption);
    } else {
      this.#syncSelection("");
    }
  }

  #ensureDefaultSelection() {
    const propertyInput = this.#getPropertyInput();
    if (!(propertyInput instanceof HTMLInputElement)) return;
    if (propertyInput.value.trim() !== "") return;

    const firstOption = this.#optionInputs[0];
    if (!(firstOption instanceof HTMLInputElement)) return;

    this.#selectOption(firstOption);
  }

  /** @param {HTMLInputElement} input */
  #selectOption(input) {
    const propertyInput = this.#getPropertyInput();
    if (!(propertyInput instanceof HTMLInputElement)) return;

    const selectedValue = input.value.trim();
    const previewImageUrl = input.dataset.previewImageUrl?.trim() ?? "";
    const previewTextColor = input.dataset.previewTextColor?.trim() ?? "";

    input.checked = true;
    this.#setPropertyValue(selectedValue);
    this.#syncSelection(selectedValue);
    clearFutCardImageOptionsError(propertyInput);
    validateFutCardImageOptionsInput(propertyInput);

    this.dispatchEvent(
      new CustomEvent("fut-card:design-change", {
        bubbles: true,
        detail: {
          value: selectedValue,
          imageUrl: previewImageUrl,
          textColor: previewTextColor,
        },
      }),
    );
  }

  /** @param {string} value */
  #setPropertyValue(value) {
    const propertyInput = this.#getPropertyInput();
    if (propertyInput instanceof HTMLInputElement) {
      propertyInput.value = value;
      propertyInput.dispatchEvent(new Event("input", { bubbles: true }));
    }
  }

  /** @param {string} value */
  #syncSelection(value) {
    const normalizedValue = value.trim();

    for (const input of this.#optionInputs) {
      const isSelected =
        normalizedValue !== "" && input.value.trim() === normalizedValue;

      input.checked = isSelected;

      const label = input.closest("[data-fut-card-image-option-label]");
      if (label instanceof HTMLElement) {
        label.dataset.selected = isSelected ? "true" : "false";
      }
    }
  }

  /** @param {Event} event */
  #handleInvalid = (event) => {
    event.preventDefault();

    const propertyInput = this.#getPropertyInput();
    if (!(propertyInput instanceof HTMLInputElement)) return;

    validateFutCardImageOptionsInput(propertyInput);

    const focusTarget =
      this.refs.optionGrid ??
      this.querySelector(".fut-card-image-options__grid");

    if (focusTarget instanceof HTMLElement) {
      focusTarget.scrollIntoView({
        block: "center",
        behavior: "smooth",
      });
    }
  };
}

if (!customElements.get("fut-card-image-options")) {
  customElements.define("fut-card-image-options", FutCardImageOptions);
}

/**
 * Handles editable fut card stats and overall rating calculation.
 *
 * @typedef {object} FutCardStatsRefs
 * @property {HTMLElement} statGrid
 * @property {HTMLElement} overallDisplay
 * @property {HTMLInputElement} overallInput
 * @property {HTMLInputElement} overallRatingProperty
 * @property {HTMLInputElement} overrideToggle
 * @property {HTMLInputElement} modeAuto
 * @property {HTMLElement} statsError
 * @extends Component<FutCardStatsRefs>
 */
class FutCardStats extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {Record<string, string[]>} */
  #fieldLabelsByLanguage = {};

  /** @type {Record<string, string[]>} */
  #goalkeeperLabelsByLanguage = {};

  /** @type {number | null} */
  #lastOverallAverage = null;

  connectedCallback() {
    super.connectedCallback();

    const stateRoot = getFutCardStatsStateRoot(this) ?? this;

    const labelsByLanguage = parseFutCardStatLabelsByLanguage(stateRoot);
    this.#fieldLabelsByLanguage = labelsByLanguage.field;
    this.#goalkeeperLabelsByLanguage = labelsByLanguage.goalkeeper;

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#bindPositionSync();
    this.syncLabelsFromPosition();
    this.#syncOverallRating({ focusCustomInput: false });

    const valueInputs = this.querySelectorAll("[data-fut-card-stat-value]");
    valueInputs.forEach((input) => {
      input.addEventListener("invalid", this.#handleInvalid, { signal });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  syncLabelsFromPosition() {
    const position = this.#getSelectedPosition();
    this.#applyStatLabelsForPosition(position);
  }

  /** @param {Event} event */
  handleStatInput(event) {
    const target = event.target;
    if (target instanceof HTMLInputElement) {
      normalizeFutCardStatValueInput(target, getFutCardStatsBounds(this));
    }

    setFutCardStatsError(this, "");
    this.#syncOverallRating({ focusCustomInput: false });
  }

  handleStatLabelInput() {
    const labelInputs = getFutCardStatLabelInputs(this);
    labelInputs.forEach((input) => {
      input.value = normalizeFutCardStatLabelValue(input.value);
    });

    this.#syncPropertyNames();
    setFutCardStatsError(this, "");
  }

  handleOverrideToggle() {
    const isOverride = Boolean(this.refs.overrideToggle?.checked);

    this.#syncOverallRating({
      focusCustomInput: isOverride,
    });
  }

  /** @param {Event} event */
  handleOverallInput(event) {
    const target = event.target;
    if (target instanceof HTMLInputElement) {
      normalizeFutCardStatValueInput(target, getFutCardStatsBounds(this));
    }

    setFutCardStatsError(this, "");
    this.#syncOverallRating({ focusCustomInput: false });
  }

  syncForSubmit() {
    validateFutCardStatsInputs(this);
  }

  reset() {
    const valueInputs = this.querySelectorAll("[data-fut-card-stat-value]");
    for (const input of valueInputs) {
      if (!(input instanceof HTMLInputElement)) continue;
      input.value = input.defaultValue;
      input.setCustomValidity("");
    }

    const labelInputs = this.querySelectorAll("[data-fut-card-stat-label]");
    for (const input of labelInputs) {
      if (!(input instanceof HTMLInputElement)) continue;
      input.value = input.defaultValue;
    }

    if (this.refs.overrideToggle instanceof HTMLInputElement) {
      this.refs.overrideToggle.checked =
        this.refs.overrideToggle.defaultChecked;
    }
    if (this.refs.modeAuto instanceof HTMLInputElement) {
      this.refs.modeAuto.checked = this.refs.modeAuto.defaultChecked;
    }
    if (this.refs.overallInput instanceof HTMLInputElement) {
      this.refs.overallInput.value = this.refs.overallInput.defaultValue;
      this.refs.overallInput.setCustomValidity("");
    }

    setFutCardStatsError(this, "");
    this.syncLabelsFromPosition();
    this.#syncOverallRating({ focusCustomInput: false });
  }

  #bindPositionSync() {
    const popup = this.closest("[data-fut-card-personalization-popup]");
    const positionRoot = popup?.querySelector("fut-card-player-position");
    const positionInput = popup?.querySelector(
      "[data-fut-card-player-position]",
    );
    const signal = this.#abortController?.signal;

    if (!signal) return;

    if (positionInput instanceof HTMLInputElement) {
      positionInput.addEventListener("input", this.#handlePositionChange, {
        signal,
      });
      positionInput.addEventListener("change", this.#handlePositionChange, {
        signal,
      });
    }

    positionRoot
      ?.querySelectorAll(
        "[data-fut-card-position-preset], [data-fut-card-position-custom]",
      )
      .forEach((input) => {
        input.addEventListener("change", this.#handlePositionChange, {
          signal,
        });
      });
  }

  #handlePositionChange = () => {
    this.syncLabelsFromPosition();
  };

  /** @returns {string} */
  #getSelectedPosition() {
    const popup = this.closest("[data-fut-card-personalization-popup]");
    const positionInput = popup?.querySelector(
      "[data-fut-card-player-position]",
    );
    return positionInput instanceof HTMLInputElement
      ? positionInput.value.trim().toUpperCase()
      : "";
  }

  /** @returns {string} */
  #getSelectedPositionLanguage() {
    const popup = this.closest("[data-fut-card-personalization-popup]");
    const positionElement = popup?.querySelector("fut-card-player-position");
    if (!(positionElement instanceof HTMLElement)) return "en";

    const stateRoot = getFutCardPlayerPositionStateRoot(positionElement);
    const lang = stateRoot?.dataset.activeLanguage || "en";

    return FUT_CARD_STAT_LABEL_LANGUAGES.includes(lang) ? lang : "en";
  }

  /**
   * @param {boolean} isGoalkeeper
   * @returns {string[]}
   */
  #getStatLabels(isGoalkeeper) {
    const lang = this.#getSelectedPositionLanguage();
    const labelsByLanguage = isGoalkeeper
      ? this.#goalkeeperLabelsByLanguage
      : this.#fieldLabelsByLanguage;

    return labelsByLanguage[lang] ?? labelsByLanguage.en ?? [];
  }

  /** @param {string} position */
  #applyStatLabelsForPosition(position) {
    const popup = this.closest("[data-fut-card-personalization-popup]");
    const isGoalkeeper = isFutCardGoalkeeperPosition(position, popup);
    const labels = this.#getStatLabels(isGoalkeeper);

    if (labels.length === 0) return;

    const labelInputs = getFutCardStatLabelInputs(this);
    const valueInputs = getFutCardStatValueInputs(this);

    labels.forEach((label, index) => {
      const labelInput = labelInputs[index];
      const valueInput = valueInputs[index];

      if (labelInput instanceof HTMLInputElement) {
        labelInput.value = label;
      }

      if (valueInput instanceof HTMLInputElement) {
        valueInput.dataset.defaultLabel = label;
      }
    });

    this.#syncPropertyNames();
    this.#syncOverallRating({ focusCustomInput: false });
  }

  #syncPropertyNames() {
    syncFutCardStatPropertyNames(this);
  }

  /**
   * @param {{ focusCustomInput?: boolean }} [options]
   */
  #syncOverallRating(options = {}) {
    const average = calculateFutCardStatsAverage(this);
    const isOverride = Boolean(this.refs.overrideToggle?.checked);
    const { min, max } = getFutCardStatsBounds(this);

    this.dataset.overallMode = isOverride ? "custom" : "auto";

    if (this.refs.modeAuto instanceof HTMLInputElement) {
      this.refs.modeAuto.checked = !isOverride;
    }

    if (this.refs.overallDisplay instanceof HTMLElement) {
      this.refs.overallDisplay.textContent = String(average);
      this.refs.overallDisplay.hidden = isOverride;
    }

    if (this.refs.overallInput instanceof HTMLInputElement) {
      if (!isOverride) {
        this.refs.overallInput.value = String(average);
        this.refs.overallInput.hidden = true;
        this.refs.overallInput.readOnly = true;
      } else {
        if (!this.refs.overallInput.value) {
          this.refs.overallInput.value = String(average);
        }
        this.refs.overallInput.hidden = false;
        this.refs.overallInput.readOnly = false;
      }

      this.refs.overallInput.min = String(min);
      this.refs.overallInput.max = String(max);
    }

    const overallValue =
      isOverride && this.refs.overallInput instanceof HTMLInputElement
        ? (parseFutCardStatValue(this.refs.overallInput) ?? average)
        : average;

    if (this.refs.overallRatingProperty instanceof HTMLInputElement) {
      this.refs.overallRatingProperty.value = String(overallValue);
    }

    if (!isOverride && this.#lastOverallAverage !== average) {
      this.#lastOverallAverage = average;
      this.dataset.overallUpdated = "true";
      window.setTimeout(() => {
        delete this.dataset.overallUpdated;
      }, 400);
    } else if (isOverride) {
      this.#lastOverallAverage = average;
    }

    if (
      options.focusCustomInput &&
      this.refs.overallInput instanceof HTMLInputElement
    ) {
      this.refs.overallInput.focus();
      this.refs.overallInput.select();
    }
  }

  /** @param {Event} event */
  #handleInvalid = (event) => {
    event.preventDefault();
    validateFutCardStatsInputs(this);

    const target = event.target;
    if (target instanceof HTMLElement) {
      target.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  };
}

if (!customElements.get("fut-card-stats")) {
  customElements.define("fut-card-stats", FutCardStats);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLButtonElement | undefined} buyNowButton - The upload-safe buy now button.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ["variantId", "liveRegion"];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {Array<{variantId: string, quantity: number}>} */
  #addToCartQueue = [];

  /** @type {boolean} */
  #addToCartInProgress = false;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const target = this.closest(
      "[data-fut-card-review-checkout], .shopify-section, dialog, product-card",
    );
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal,
    });
    target?.addEventListener(
      ThemeEvents.variantSelected,
      this.#onVariantSelected,
      { signal },
    );

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, {
      signal,
    });

    // Clear buy-now loading state on bfcache restore (back from checkout)
    window.addEventListener("pageshow", this.#onPageShow, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="id"]')
    );
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find(
      (item) => item.variant_id.toString() === variantIdInput.value.toString(),
    );
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (
      this.querySelector("quantity-selector-component")
    );
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches the current cart and updates the quantity display.
   * @returns {Promise<Cart | null>} The cart object, or null if fetch fails.
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="id"]')
    );
    if (!variantIdInput?.value) return null;

    try {
      const response = await fetch("/cart.js");
      const cart = await response.json();

      this.#updateCartQuantityFromData(cart);
      return cart;
    } catch (error) {
      console.error("Failed to fetch cart quantity:", error);
      return null;
    }
  }

  #onPageShow = () => {
    this.#setBuyNowLoading(false);
  };

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (
      event.detail?.sourceId === this.id ||
      event.detail?.data?.source === "product-form-component"
    )
      return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    const form = this.querySelector("form");
    if (!form) return;

    normalizeProductLineItemInputs(form);

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    if (this.#variantChangeInProgress) {
      const hasFileInput = Array.from(form.elements).some(
        (element) =>
          element instanceof HTMLInputElement && element.type === "file",
      );
      if (hasFileInput) return;

      const intendedVariantId = this.#getIntendedVariantId();
      const quantity = this.#getQuantity();

      if (intendedVariantId) {
        this.#addToCartQueue.push({ variantId: intendedVariantId, quantity });
      }

      this.#setAddToCartLoading(true);
      return;
    }

    if (this.#addToCartInProgress) return;

    void this.#processAddToCart(undefined, undefined, event);
  }

  /** @param {Event} event */
  handleBuyNow(event) {
    event.preventDefault();

    if (this.refs.buyNowButton?.dataset.loading === "true") return;

    const form = this.querySelector("form");
    if (!form) return;

    normalizeProductLineItemInputs(form);

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    void this.#processBuyNow(event);
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    return (
      new URL(window.location.href).searchParams.get("variant") ||
      this.refs.variantId?.value ||
      undefined
    );
  }

  /** @returns {number} */
  #getQuantity() {
    return (
      Number(this.refs.quantitySelector?.getValue?.()) ||
      Number(this.dataset.quantityDefault) ||
      1
    );
  }

  /** @param {Event} event */
  #processBuyNow(event) {
    const { addToCartTextError } = this.refs;
    const form = this.querySelector("form");
    if (!form) throw new Error("Product form element missing");
    let isRedirectingToCheckout = false;

    normalizeProductLineItemInputs(form);

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    this.#setBuyNowLoading(true);

    const formData = new FormData(form);
    const fetchCfg = fetchConfig("javascript", { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: "application/json",
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(
              form.getAttribute("id") || "",
              response.message,
              response.description,
              response.errors,
            ),
          );

          if (addToCartTextError) {
            addToCartTextError.classList.remove("hidden");
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = response.message;
            } else {
              addToCartTextError.appendChild(
                document.createTextNode(response.message),
              );
            }
            this.#setLiveRegionText(response.message);
          }

          return;
        }

        this.#dispatchProductFormReset();
        isRedirectingToCheckout = true;
        window.location.href = "/checkout";
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (!isRedirectingToCheckout) this.#setBuyNowLoading(false);
        cartPerformance.measureFromEvent("add:user-action", event);
      });
  }

  /** @param {boolean} loading */
  #setBuyNowLoading(loading) {
    const { buyNowButton } = this.refs;
    if (!buyNowButton) return;

    buyNowButton.disabled = loading;
    buyNowButton.dataset.loading = loading ? "true" : "false";
    buyNowButton.setAttribute("aria-busy", loading ? "true" : "false");
  }

  /** @param {boolean} loading */
  #setAddToCartLoading(loading) {
    const allAddToCartContainers =
      /** @type {NodeListOf<AddToCartComponent>} */ (
        this.querySelectorAll("add-to-cart-component")
      );

    for (const container of allAddToCartContainers) {
      container.setLoading(loading);
    }
  }

  /** @param {boolean} [includePuppet] */
  #showAddToCartSuccessAnimation(includePuppet = false) {
    const allAddToCartContainers =
      /** @type {NodeListOf<AddToCartComponent>} */ (
        this.querySelectorAll("add-to-cart-component")
      );

    for (const container of allAddToCartContainers) {
      const { addToCartButton } = container.refs;
      if (!includePuppet && addToCartButton?.dataset.puppet === "true")
        continue;
      container.showSuccessAnimation();
    }
  }

  #dispatchProductFormReset() {
    if (this.dataset.uploadCheckout !== "true") return;

    const form = this.querySelector("form");
    if (!form) return;

    const resettables = new Set();
    for (const el of form.elements) {
      if (!(el instanceof HTMLElement)) continue;
      let parent = el.parentElement;
      while (parent && parent !== document.body) {
        if (
          parent.tagName.includes("-") &&
          !(parent instanceof HTMLFormElement) &&
          typeof (/** @type {any} */ (parent).reset) === "function"
        ) {
          resettables.add(parent);
          break;
        }
        parent = parent.parentElement;
      }
    }
    for (const el of resettables) {
      try {
        /** @type {{ reset?: () => void }} */ (el).reset?.();
      } catch (error) {
        console.warn("Failed to reset personalization element", el, error);
      }
    }

    document.dispatchEvent(new Event("product-form:reset"));
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers =
      /** @type {NodeListOf<AddToCartComponent>} */ (
        this.querySelectorAll("add-to-cart-component")
      );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled,
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector("form");
    if (!form) throw new Error("product form element missing");

    normalizeProductLineItemInputs(form);

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || "";
        const errorMessage = errorTemplate.replace(
          "{{ maximum }}",
          validation.maxQuantity?.toString() || "",
        );
        if (addToCartTextError) {
          addToCartTextError.classList.remove("hidden");

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add("hidden");
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    this.#addToCartInProgress = true;
    this.#setAddToCartLoading(true);

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set("id", overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set("quantity", overrideQuantity.toString());
    }

    const cartItemsComponents = document.querySelectorAll(
      "cart-items-component",
    );
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append("sections", cartItemComponentsSectionIds.join(","));
    });

    const fetchCfg = fetchConfig("javascript", { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: "text/html",
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(
              form.getAttribute("id") || "",
              response.message,
              response.description,
              response.errors,
            ),
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove("hidden");

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add("hidden");

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: "product-form-component",
              itemCount:
                Number(formData.get("quantity")) ||
                Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            }),
          );

          return;
        } else {
          const id = formData.get("id");

          if (addToCartTextError) {
            addToCartTextError.classList.add("hidden");
            addToCartTextError.removeAttribute("aria-live");
          }

          if (!id) throw new Error("Form ID is required");

          this.#showAddToCartSuccessAnimation();
          this.#dispatchProductFormReset();

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton =
            allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector(
              ".add-to-cart-text--added",
            );
            const addedText =
              addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          const cart = await this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent(cart ?? undefined, id.toString(), {
              source: "product-form-component",
              itemCount:
                Number(formData.get("quantity")) ||
                Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            }),
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#addToCartInProgress = false;
        this.#setAddToCartLoading(false);
        if (event) {
          cartPerformance.measureFromEvent("add:user-action", event);
        }
      });
  }

  /** @param {Array<{variantId: string, quantity: number}>} items */
  #processBatchAddToCart(items) {
    if (items.length === 0) return;

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const cartItemsComponents = document.querySelectorAll(
      "cart-items-component",
    );
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
      })),
      sections: cartItemComponentsSectionIds.join(","),
    };

    this.#addToCartInProgress = true;
    this.#setAddToCartLoading(true);

    fetch(Theme.routes.cart_add_url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(
              this.id,
              response.message,
              response.description,
              response.errors,
            ),
          );

          if (addToCartTextError) {
            addToCartTextError.classList.remove("hidden");
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = response.message;
            } else {
              addToCartTextError.appendChild(
                document.createTextNode(response.message),
              );
            }
            this.#setLiveRegionText(response.message);

            this.#timeout = setTimeout(() => {
              addToCartTextError.classList.add("hidden");
              this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);
          }

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: "product-form-component",
              itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
              productId: this.dataset.productId,
            }),
          );
          return;
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add("hidden");
          addToCartTextError.removeAttribute("aria-live");
        }

        this.#showAddToCartSuccessAnimation();

        const allAddToCartContainers =
          /** @type {NodeListOf<AddToCartComponent>} */ (
            this.querySelectorAll("add-to-cart-component")
          );
        const anyAddToCartButton =
          allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector(
            ".add-to-cart-text--added",
          );
          const addedText =
            addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(
            () => this.#clearLiveRegionText(),
            SUCCESS_MESSAGE_DISPLAY_DURATION,
          );
        }

        const cart = await this.#fetchAndUpdateCartQuantity();

        const totalQuantity = items.reduce(
          (sum, item) => sum + item.quantity,
          0,
        );
        this.dispatchEvent(
          new CartAddEvent(cart ?? undefined, this.id, {
            source: "product-form-component",
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            sections: response.sections,
          }),
        );
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#addToCartInProgress = false;
        this.#setAddToCartLoading(false);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle("hidden", cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = "";
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(
    currentElement,
    newElement,
    insertReferenceElement = null,
  ) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement(
        "beforebegin",
        /** @type {Element} */ (newElement.cloneNode(true)),
      );
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    variantId.value = event.detail.resource?.id ?? "";

    this.#variantChangeInProgress = false;

    if (this.#addToCartQueue.length > 0) {
      const queuedItems = [...this.#addToCartQueue];
      this.#addToCartQueue = [];
      this.#processBatchAddToCart(queuedItems);
    }

    const {
      addToCartButtonContainer: currentAddToCartButtonContainer,
      acceleratedCheckoutButtonContainer,
    } = this.refs;
    const currentAddToCartButton =
      currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (
      !currentAddToCartButtonContainer ||
      (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)
    )
      return;

    // Update the button state
    if (
      event.detail.resource == null ||
      event.detail.resource.available == false
    ) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector(
      'product-form-component [ref="addToCartButton"]',
    );
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (
        event.detail.resource == null ||
        event.detail.resource.available == false
      ) {
        acceleratedCheckoutButtonContainer?.setAttribute("hidden", "true");
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute("hidden");
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia =
        event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          "data-product-variant-media",
          productVariantMedia + "&width=100",
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector(
        'quantity-selector-component input[ref="quantityInput"]',
      )
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(
        newQuantityInput.min,
        newQuantityInput.max || null,
        newQuantityInput.step,
      );
    }

    const newQuantityRules =
      event.detail.data.html.querySelector(".quantity-rules");
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem =
      event.detail.data.html.querySelector("price-per-item");
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if (
      (isQuantityRulesChanging || isPricePerItemChanging) &&
      quantitySelector
    ) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector(
        ".product-form-buttons",
      );

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector(
            'quantity-selector-component input[ref="quantityInput"]',
          )
        );

        if (
          this.refs.quantitySelector?.updateConstraints &&
          newQuantityInputElement &&
          currentQuantityValue
        ) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step,
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        [".quantity-label", quantityLabel, quantitySelector],
        [".quantity-rules", quantityRules, this.refs.productFormButtons],
        ["price-per-item", pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(
          currentElement,
          event.detail.data.html.querySelector(selector),
          fallback,
        );
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing =
      event.detail.data.html.querySelector("volume-pricing");
    this.#morphOrUpdateElement(
      currentVolumePricing,
      newVolumePricing,
      this.refs.productFormButtons,
    );

    const hasB2BFeatures =
      quantityRules ||
      newQuantityRules ||
      pricePerItem ||
      newPricePerItem ||
      currentVolumePricing ||
      newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {import('./events').VariantSelectedEvent} _event */
  #onVariantSelected = (_event) => {
    this.#variantChangeInProgress = true;
  };
}

if (!customElements.get("product-form-component")) {
  customElements.define("product-form-component", ProductFormComponent);
}
